import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "./App.css";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import OnboardingWizard, { writeOnboardingComplete } from "./OnboardingWizard";
import BacktestChart, { BacktestResult } from "./BacktestChart";
import DryRunPanel, { DryRunResult } from "./DryRunPanel";
import {
  API_BASE,
  clearStoredToken,
  readStoredToken,
  writeStoredToken,
} from "./api";

type BacktestRegion = "ERCOT" | "PJM" | "CAISO";

const BACKTEST_DEFAULT_THRESHOLDS: Record<BacktestRegion, number> = {
  ERCOT: 100,
  PJM: 150,
  CAISO: 120,
};

type AuthScreen = "login" | "forgot" | "reset" | "request-access";

const authInputStyle: React.CSSProperties = {
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #334155",
  background: "#0B1220",
  color: "#F8FAFC",
};

const authLinkStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#5EEAD4",
  fontSize: 13,
  cursor: "pointer",
  textDecoration: "underline",
};

type MarketRegion = "ERCOT" | "PJM" | "CAISO";

const DEFAULT_SPIKE_THRESHOLD_BY_REGION: Record<MarketRegion, number> = {
  ERCOT: 100,
  PJM: 150,
  CAISO: 120,
};

function normalizeMarketRegion(value: string | undefined | null): MarketRegion {
  if (value === "PJM" || value === "CAISO" || value === "ERCOT") return value;
  return "ERCOT";
}

function formatLastUpdated(when: Date | null): string {
  if (!when) return "--";
  return when.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatSavingsHeadline(value: number | null | undefined): string {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "$0";
  const abs = Math.abs(n);
  if (abs >= 100_000) {
    const compact = new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: abs >= 1_000_000 ? 1 : 0,
    }).format(n);
    return `$${compact}`;
  }
  return `$${n.toFixed(2)}`;
}

interface UserInfo {
  id: number;
  email: string;
  company_name: string;
  plan: string;
  onboarding_completed: boolean;
  is_admin?: boolean;
}

interface AgentStatusBlock {
  token_exists: boolean;
  is_online?: boolean;
  last_seen?: string | null;
  total_events?: number;
}

interface AgentEventRow {
  event_type: string;
  timestamp: string;
  payload: Record<string, unknown>;
}

interface AgentDetail {
  connected: boolean;
  token_exists: boolean;
  is_online: boolean;
  token_name: string | null;
  last_seen: string | null;
  mode: string | null;
  scheduler: string | null;
  webhook_registered: boolean;
  webhook_last_push: string | null;
  total_events: number;
  events: AgentEventRow[];
  response_time_avg_ms: number;
  response_time_best_ms: number | null;
  response_time_recent_ms: number[];
  uptime_pct: number;
  total_pauses: number;
  total_resumes: number;
  jobs_currently_paused: string[];
  last_action: string | null;
  last_action_time: string | null;
}

function formatAgentResponseLabel(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms) || ms <= 0) return "—";
  if (ms >= 1000) return `Avg ${(ms / 1000).toFixed(1)}s response`;
  return `Avg ${Math.round(ms)}ms response`;
}

function agentModeBadge(mode: string | null | undefined): { label: string; color: string; bg: string; border: string } {
  const m = (mode || "automatic").toLowerCase();
  if (m.includes("semi")) {
    return { label: "Semi-Auto", color: "#FDE68A", bg: "#422006", border: "#D97706" };
  }
  if (m.includes("manual")) {
    return { label: "Manual", color: "#CBD5E1", bg: "#1E293B", border: "#475569" };
  }
  return { label: "Auto", color: "#A7F3D0", bg: "#064E3B", border: "#059669" };
}

function agentEventRowStyle(eventType: string): React.CSSProperties {
  if (eventType === "pause_executed") return { background: "rgba(234, 88, 12, 0.12)" };
  if (eventType === "resume_executed") return { background: "rgba(34, 197, 94, 0.12)" };
  if (eventType === "error") return { background: "rgba(239, 68, 68, 0.12)" };
  return {};
}

function formatAgentEventLabel(eventType: string): string {
  if (eventType === "pause_executed") return "Pause";
  if (eventType === "resume_executed") return "Resume";
  if (eventType === "error") return "Error";
  return eventType.replace(/_/g, " ");
}

type JobPriority = "CRITICAL" | "FLEXIBLE";
type JobStatus = "RUNNING" | "PAUSED";

interface Job {
  id: string;
  name: string;
  priority: JobPriority;
  power_kw: number;
  status: JobStatus;
  created_at: string;
}

interface PriceSignal {
  price_per_mwh: number;
  timestamp: string;
  is_spike: boolean;
  carbon_intensity: number;
}

interface OptimizationResult {
  timestamp: string;
  price_per_mwh: number;
  actions_taken: string[];
  kw_saved: number;
  estimated_savings_usd: number;
  cumulative_savings_usd: number;
}

interface SavingsResponse {
  total_cumulative_savings_usd: number;
  savings_today_usd: number;
  co2_avoided_kg: number;
  co2_epa_spike_kg_today?: number;
  carbon_credits_earned_today?: number;
  spike_events_handled: number;
}

interface CarbonSummaryResponse {
  credit_price_usd: number;
  month_co2_kg: number;
  month_co2_metric_tons: number;
  month_carbon_credits: number;
  month_carbon_value_usd: number;
  cumulative_carbon_credits: number;
  cumulative_carbon_value_usd: number;
  cumulative_co2_kg: number;
  cumulative_co2_metric_tons: number;
  today_co2_kg: number;
  today_carbon_credits: number;
  progress_toward_next_credit_pct: number;
}

interface FeedStatus {
  active_feed: string;
  last_successful_api_call: string | null;
  error_message: string | null;
  degraded: boolean;
  effective_price_source: string;
  market_region?: string;
  watchdog_active?: boolean;
  watchdog_last_triggered?: string | null;
  watchdog_checks_today?: number;
  watchdog_setting_enabled?: boolean;
  watchdog_sensitivity_pct?: number;
  watchdog_absolute_trigger_mwh?: number;
}

interface ForecastPoint {
  hour: string;
  score: number;
  band: "low" | "elevated" | "high" | "imminent";
}

const FORECAST_BAND_BAR_FILL: Record<ForecastPoint["band"], string> = {
  low: "#22c55e",
  elevated: "#eab308",
  high: "#f97316",
  imminent: "#ef4444",
};

const FORECAST_BAND_BADGE: Record<ForecastPoint["band"], { color: string; background: string }> = {
  low: { color: "#BBF7D0", background: "#052E16" },
  elevated: { color: "#FDE047", background: "#422006" },
  high: { color: "#FDBA74", background: "#431407" },
  imminent: { color: "#FCA5A5", background: "#450A0A" },
};

interface ForecastResponse {
  current_score: number;
  current_band: "low" | "elevated" | "high" | "imminent";
  factors: Record<string, number | string>;
  curve_24h: ForecastPoint[];
}

interface ForecastHistoryItem {
  timestamp: string;
  predicted_probability: number;
  actual_spike: boolean | null;
  factors_breakdown: Record<string, unknown>;
}

function forecastFactorNumeric(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

type ForecastRiskTier = "Low" | "Moderate" | "High";

function forecastRiskTier(score: number): ForecastRiskTier {
  if (score <= 30) return "Low";
  if (score <= 60) return "Moderate";
  return "High";
}

/** Customer-facing lines only — no internal keys or raw metrics. */
function buildForecastFactorRows(
  factors: Record<string, number | string> | undefined
): Array<{ label: string; description: string }> {
  if (!factors) return [];

  const hourRisk = forecastFactorNumeric(factors.hour_of_day);
  const dowRisk = forecastFactorNumeric(factors.day_of_week);
  const monthRisk = forecastFactorNumeric(factors.month_season);
  const volRisk = forecastFactorNumeric(factors.recent_volatility);
  const rawMomentumPct = forecastFactorNumeric(factors.raw_momentum_pct_6h);

  const hourTier = forecastRiskTier(hourRisk);
  const monthTier = forecastRiskTier(monthRisk);

  const dowKind = forecastRiskTier(dowRisk);
  const dowPattern =
    dowKind === "Low"
      ? "lower risk pattern"
      : dowKind === "Moderate"
      ? "moderate risk pattern"
      : "higher risk pattern";

  const now = new Date();
  const isWeekend = now.getDay() === 0 || now.getDay() === 6;
  const weekdayLabel = isWeekend ? "Weekend" : "Weekday";

  let momentumPhrase: string;
  if (rawMomentumPct > 5) momentumPhrase = "Rising fast";
  else if (rawMomentumPct < -5) momentumPhrase = "Falling";
  else momentumPhrase = "Stable";

  const volTier = forecastRiskTier(volRisk);
  const volatilityPhrase =
    volTier === "Low" ? "Low" : volTier === "Moderate" ? "Moderate" : "High";

  return [
    {
      label: "Time of day",
      description: `${hourTier} historical spike rate for this hour`,
    },
    {
      label: "Day of week",
      description: `${weekdayLabel} — ${dowPattern}`,
    },
    {
      label: "Seasonal pattern",
      description: `${monthTier} risk for this time of year`,
    },
    {
      label: "Price momentum",
      description: `${momentumPhrase} over the last 6 hours`,
    },
    {
      label: "Recent volatility",
      description: `${volatilityPhrase} over the last 6 hours`,
    },
  ];
}

interface AuditLogItem {
  id: number;
  user_id: number | null;
  timestamp: string;
  event_type: string;
  actor: string;
  description: string;
  meta?: Record<string, unknown> | null;
  ip_address?: string | null;
}

type AuditFilterPreset = "all" | "system" | "human" | "settings";

function auditAccent(event: AuditLogItem): { rowBg: string; badgeBg: string } {
  const et = event.event_type;
  if (event.meta && typeof event.meta === "object" && "error" in event.meta) {
    return { rowBg: "rgba(239,68,68,0.14)", badgeBg: "#DC2626" };
  }
  if (et === "SETTINGS_CHANGED" || et === "WATCHDOG_SETTINGS_CHANGED") {
    return { rowBg: "rgba(234,179,8,0.1)", badgeBg: "#CA8A04" };
  }
  if (et === "OPTIMIZER_RUN" || et === "WATCHDOG_TRIGGERED") {
    return { rowBg: "rgba(34,197,94,0.1)", badgeBg: "#15803D" };
  }
  if (
    et === "JOB_PAUSED" ||
    et === "JOB_RESUMED" ||
    et === "USER_LOGIN" ||
    et === "USER_LOGOUT" ||
    et === "REPORT_GENERATED"
  ) {
    return { rowBg: "rgba(99,102,241,0.12)", badgeBg: "#4F46E5" };
  }
  if (et === "SYSTEM_STARTED" || et === "SYSTEM_STOPPED") {
    return { rowBg: "rgba(59,130,246,0.12)", badgeBg: "#2563EB" };
  }
  return { rowBg: "rgba(15,23,42,0.45)", badgeBg: "#64748B" };
}

function formatLastApiSuccessTooltip(activeFeed: string, iso: string | null): string {
  if (activeFeed === "simulated") {
    return "Live API not configured; using simulated prices.";
  }
  const label =
    activeFeed === "ercot_direct"
      ? "Last successful ERCOT API call (UTC)"
      : "Last successful EIA API call (UTC)";
  if (!iso) return `${label}: none yet`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return `${label}: ${iso}`;
  return `${label}: ${d.toISOString().replace("T", " ").slice(0, 19)}`;
}

function dataSourceLabel(src: string | undefined): string {
  if (src === "ercot") return "ERCOT (real-time LMP)";
  if (src === "eia") return "EIA (hourly)";
  if (src === "simulated") return "Simulated demo data";
  return "Unknown";
}

function DataFeedIndicator({ status }: { status: FeedStatus | null }) {
  const degraded = status?.degraded === true;
  const src = status?.effective_price_source ?? "unknown";
  const ercotDirect = status?.active_feed === "ercot_direct";
  const real = status?.active_feed === "real";
  const simulated = status?.active_feed === "simulated";
  const liveEiaFallback = degraded && src === "eia";

  let dotColor = "#64748B";
  let label = "…";
  let labelColor = "#94A3B8";
  if (liveEiaFallback) {
    dotColor = "#F59E0B";
    label = "LIVE (EIA)";
    labelColor = "#FDE68A";
  } else if (degraded) {
    dotColor = "#EF4444";
    label = "DEGRADED";
    labelColor = "#FECACA";
  } else if (ercotDirect) {
    dotColor = "#22C55E";
    label = "ERCOT DIRECT";
    labelColor = "#BBF7D0";
  } else if (real) {
    dotColor = "#22C55E";
    label = "LIVE DATA";
    labelColor = "#BBF7D0";
  } else if (simulated) {
    dotColor = "#EAB308";
    label = "SIMULATED";
    labelColor = "#FEF08A";
  } else if (status) {
    dotColor = "#94A3B8";
    label = status.active_feed.toUpperCase();
    labelColor = "#CBD5E1";
  }

  const tooltip = status
    ? [
        `Latest price from: ${dataSourceLabel(status.effective_price_source)}`,
        formatLastApiSuccessTooltip(status.active_feed, status.last_successful_api_call),
        status.degraded && status.error_message ? status.error_message : null,
      ]
        .filter(Boolean)
        .join("\n")
    : "Loading data feed status…";

  const liveGlow = ercotDirect || real || liveEiaFallback;

  return (
    <div
      title={tooltip}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px 3px 8px",
        borderRadius: 8,
        background: "rgba(15, 23, 42, 0.65)",
        border: "1px solid #1F355B",
        cursor: "help",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: dotColor,
          boxShadow: liveEiaFallback
            ? "0 0 8px #F59E0B"
            : degraded
              ? "0 0 8px #EF4444"
              : liveGlow
                ? "0 0 8px #22C55E"
                : simulated
                  ? "0 0 6px #EAB308"
                  : "none",
        }}
      />
      <span style={{ fontSize: 11, fontWeight: 700, color: labelColor, letterSpacing: 0.4 }}>{label}</span>
    </div>
  );
}

function WatchdogIndicator({ status }: { status: FeedStatus | null }) {
  const active = status?.watchdog_active === true;
  const lastIso = status?.watchdog_last_triggered;
  const checks = status?.watchdog_checks_today;
  const lastText =
    lastIso != null && lastIso.length > 0
      ? `Last spike response: ${new Date(lastIso).toLocaleString()}`
      : "No watchdog-triggered run yet (or none this session).";
  const samples =
    typeof checks === "number" ? `30s price checks today (UTC): ${checks}` : null;
  const tooltip = !status
    ? "Loading spike watchdog status…"
    : [active ? "Spike Watchdog: 30s checks when auto-optimization is on." : "Watchdog idle — enable automatic optimization in Settings.", lastText, samples]
        .filter(Boolean)
        .join("\n");

  const color = active ? "#22C55E" : "#64748B";

  return (
    <div
      title={tooltip}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px 3px 8px",
        borderRadius: 8,
        background: "rgba(15, 23, 42, 0.65)",
        border: "1px solid #1F355B",
        cursor: "help",
      }}
    >
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        aria-hidden
        style={{ color, flexShrink: 0 }}
      >
        <path
          fill="currentColor"
          d="M12 2 4 5v6.09c0 4.91 3.5 9.45 8 10.91 4.5-1.46 8-6 8-10.91V5l-8-3zm0 2.18 6 2.25v5.15c0 3.45-2.33 6.64-6 7.9-3.67-1.26-6-4.45-6-7.9V6.43l6-2.25z"
        />
      </svg>
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: active ? "#BBF7D0" : "#94A3B8",
          letterSpacing: 0.3,
        }}
      >
        {active ? "Watchdog Active" : "Watchdog off"}
      </span>
    </div>
  );
}

interface OptimizationEvent {
  timestamp: string;
  price_per_mwh: number;
  is_spike: boolean;
  kw_saved: number;
  estimated_savings_usd: number;
  cumulative_savings_usd: number;
  actions_taken: string[];
}

interface SettingsPayload {
  price_threshold: number;
  auto_optimization_enabled: boolean;
  max_pause_duration: string;
  blackout_start_hour: number | null;
  blackout_end_hour: number | null;
  email_alerts_enabled: boolean;
  slack_notifications_enabled: boolean;
  notification_target: string;
  notification_email: string;
  billing_contact_email?: string;
  fee_percentage?: number;
  job_priorities: Record<string, JobPriority>;
  watchdog_enabled: boolean;
  watchdog_sensitivity_pct: number;
  watchdog_absolute_trigger: number;
  carbon_credit_price?: number;
  market_region: MarketRegion;
}

interface MonthlyReportEventItem {
  timestamp: string;
  price_per_mwh: number;
  is_spike: boolean;
  kw_saved: number;
  estimated_savings_usd: number;
  actions_taken: string[];
}

interface MonthlyReportDailySavingsItem {
  date: string;
  savings_usd: number;
}

interface MonthlyReportResponse {
  month: string;
  generated_at: string;
  company_name: string;
  invoice_reference: string;
  billing_period_label: string;
  total_savings_usd: number;
  total_spike_events: number;
  total_co2_avoided_kg: number;
  month_carbon_credits?: number;
  month_carbon_value_usd?: number;
  month_co2_epa_spike_kg?: number;
  cumulative_carbon_credits?: number;
  cumulative_carbon_value_usd?: number;
  trees_equivalent_annual?: number;
  avg_market_price_per_mwh: number;
  avg_effective_price_paid_per_mwh: number;
  top_expensive_events_handled: MonthlyReportEventItem[];
  daily_savings_breakdown: MonthlyReportDailySavingsItem[];
  event_log: MonthlyReportEventItem[];
  routine_monitoring_checks_omitted: number;
}

interface BillingInvoice {
  invoice_id: string;
  customer_id: number;
  billing_period: string;
  total_savings: number;
  fee_percentage: number;
  fee_amount: number;
  status: "draft" | "sent" | "paid" | "overdue";
  created_at: string;
  sent_at: string | null;
  due_date: string | null;
  billing_contact_email: string | null;
}

const cardStyle: React.CSSProperties = {
  background: "#13213A",
  border: "1px solid #1F355B",
  borderRadius: 14,
  padding: "18px 20px",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.24)",
};

const statValueStyle: React.CSSProperties = {
  marginTop: 8,
  fontSize: "clamp(1.15rem, 5vw, 30px)",
  fontWeight: 800,
  lineHeight: 1.15,
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resetTokenParam = searchParams.get("token")?.trim() || "";

  const [accessToken, setAccessToken] = useState<string | null>(() => readStoredToken());
  const [authChecked, setAuthChecked] = useState(() => !readStoredToken());
  const [user, setUser] = useState<UserInfo | null>(null);
  const [authScreen, setAuthScreen] = useState<AuthScreen>(() => {
    if (location.pathname === "/reset-password") return "reset";
    if (location.pathname === "/forgot-password") return "forgot";
    if (location.pathname === "/request-access") return "request-access";
    return "login";
  });
  const [authEmail, setAuthEmail] = useState("");
  const [authPassword, setAuthPassword] = useState("");
  const [authError, setAuthError] = useState<string | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState(false);

  const [requestEmail, setRequestEmail] = useState("");
  const [requestCompany, setRequestCompany] = useState("");
  const [requestUseCase, setRequestUseCase] = useState("GPU Cloud");
  const [requestGpuCount, setRequestGpuCount] = useState("1-50");
  const [requestRegion, setRequestRegion] = useState("ERCOT");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestSuccess, setRequestSuccess] = useState(false);

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotSuccessEmail, setForgotSuccessEmail] = useState<string | null>(null);
  const [forgotError, setForgotError] = useState<string | null>(null);

  const [resetTokenValid, setResetTokenValid] = useState<boolean | null>(null);
  const [resetTokenChecking, setResetTokenChecking] = useState(false);
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentPrice, setCurrentPrice] = useState<PriceSignal | null>(null);
  const [priceHistory, setPriceHistory] = useState<PriceSignal[]>([]);
  const [savings, setSavings] = useState<SavingsResponse | null>(null);
  const [lastOptimization, setLastOptimization] = useState<OptimizationResult | null>(null);
  const [eventLog, setEventLog] = useState<OptimizationEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [livePulseOn, setLivePulseOn] = useState(true);
  const [feedStatus, setFeedStatus] = useState<FeedStatus | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [clockMs, setClockMs] = useState<number>(() => Date.now());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState<string | null>(null);
  const [savedPriceThreshold, setSavedPriceThreshold] = useState(100);
  const [priceThreshold, setPriceThreshold] = useState(100);
  const [savedMarketRegion, setSavedMarketRegion] = useState<MarketRegion>("ERCOT");
  const [marketRegion, setMarketRegion] = useState<MarketRegion>("ERCOT");
  const [autoOptimizationEnabled, setAutoOptimizationEnabled] = useState(true);
  const [maxPauseDuration, setMaxPauseDuration] = useState("unlimited");
  const [blackoutStartHour, setBlackoutStartHour] = useState<number | null>(9);
  const [blackoutEndHour, setBlackoutEndHour] = useState<number | null>(17);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(false);
  const [slackNotificationsEnabled, setSlackNotificationsEnabled] = useState(false);
  const [notificationTarget, setNotificationTarget] = useState("");
  const [notificationEmail, setNotificationEmail] = useState("");
  const [jobPriorityOverrides, setJobPriorityOverrides] = useState<Record<string, JobPriority>>({});
  const [reportDownloading, setReportDownloading] = useState(false);
  const [watchdogEnabled, setWatchdogEnabled] = useState(true);
  const [watchdogSensitivityPct, setWatchdogSensitivityPct] = useState(50);
  const [watchdogAbsoluteTrigger, setWatchdogAbsoluteTrigger] = useState(500);
  const [billingContactEmail, setBillingContactEmail] = useState("");
  const [feePercentage, setFeePercentage] = useState(17.5);
  const [billingInvoices, setBillingInvoices] = useState<BillingInvoice[]>([]);
  const settingsWatchdogHydrated = useRef(false);
  const [auditEvents, setAuditEvents] = useState<AuditLogItem[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState<AuditFilterPreset>("all");
  const [auditFromDate, setAuditFromDate] = useState("");
  const [auditToDate, setAuditToDate] = useState("");
  const [jobActionPending, setJobActionPending] = useState<Record<string, boolean>>({});
  const [jobActionErrors, setJobActionErrors] = useState<Record<string, string>>({});
  const [jobActionSuccess, setJobActionSuccess] = useState<Record<string, string>>({});
  const jobActionSuccessTimers = useRef<Record<string, number>>({});
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [setupGuideOpen, setSetupGuideOpen] = useState(false);
  const [agentStatus, setAgentStatus] = useState<AgentStatusBlock>({ token_exists: false });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showExactSavingsHeadline, setShowExactSavingsHeadline] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [forecast, setForecast] = useState<ForecastResponse | null>(null);
  const [forecastHistory, setForecastHistory] = useState<ForecastHistoryItem[]>([]);
  const [carbonSummary, setCarbonSummary] = useState<CarbonSummaryResponse | null>(null);
  const [carbonCreditPrice, setCarbonCreditPrice] = useState(20);
  const auditSectionRef = useRef<HTMLDivElement | null>(null);
  const [agentDetail, setAgentDetail] = useState<AgentDetail | null>(null);
  const [agentDetailLoading, setAgentDetailLoading] = useState(false);
  const [showAgentActivity, setShowAgentActivity] = useState(false);
  const [agentActivityPage, setAgentActivityPage] = useState(1);
  const [agentSetupOpen, setAgentSetupOpen] = useState(false);
  const [agentSetupToken, setAgentSetupToken] = useState<string | null>(null);
  const [agentSetupBusy, setAgentSetupBusy] = useState(false);
  const [agentVerifyMessage, setAgentVerifyMessage] = useState<string | null>(null);
  const [agentVerifying, setAgentVerifying] = useState(false);
  const agentVerifyTimerRef = useRef<number | null>(null);
  const [backtestModalOpen, setBacktestModalOpen] = useState(false);
  const [backtestRegion, setBacktestRegion] = useState<BacktestRegion>("ERCOT");
  const [backtestClusterMw, setBacktestClusterMw] = useState(20);
  const [backtestThreshold, setBacktestThreshold] = useState(BACKTEST_DEFAULT_THRESHOLDS.ERCOT);
  const [backtestMonths, setBacktestMonths] = useState(12);
  const [backtestRunning, setBacktestRunning] = useState(false);
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestError, setBacktestError] = useState<string | null>(null);
  const [dryRunModalOpen, setDryRunModalOpen] = useState(false);
  const [dryRunMonth, setDryRunMonth] = useState(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return `${prevMonth.getFullYear()}-${String(prevMonth.getMonth() + 1).padStart(2, "0")}`;
  });
  const [dryRunClusterMw, setDryRunClusterMw] = useState(20);
  const [dryRunThreshold, setDryRunThreshold] = useState(100);
  const [dryRunRunning, setDryRunRunning] = useState(false);
  const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
  const [dryRunError, setDryRunError] = useState<string | null>(null);

  const criticalCount = useMemo(
    () => jobs.filter((job) => job.priority === "CRITICAL").length,
    [jobs]
  );

  const effectiveJobPriorities = useMemo(() => {
    const resolved: Record<string, JobPriority> = {};
    for (const job of jobs) {
      resolved[job.id] = jobPriorityOverrides[job.id] ?? job.priority;
    }
    return resolved;
  }, [jobs, jobPriorityOverrides]);

  const totalAuditPages = useMemo(() => Math.max(1, Math.ceil(auditEvents.length / 15)), [auditEvents.length]);
  const pagedAuditEvents = useMemo(() => {
    const start = (auditPage - 1) * 15;
    return auditEvents.slice(start, start + 15);
  }, [auditEvents, auditPage]);

  const agentActivityEvents = useMemo(() => {
    const events = agentDetail?.events ?? [];
    return [...events]
      .filter((e) =>
        ["pause_executed", "resume_executed", "error"].includes(e.event_type)
      )
      .reverse()
      .slice(0, 20);
  }, [agentDetail?.events]);

  const agentActivityTotalPages = useMemo(
    () => Math.max(1, Math.ceil(agentActivityEvents.length / 10)),
    [agentActivityEvents.length]
  );

  const pagedAgentActivity = useMemo(() => {
    const start = (agentActivityPage - 1) * 10;
    return agentActivityEvents.slice(start, start + 10);
  }, [agentActivityEvents, agentActivityPage]);

  const priceColor = useMemo(() => {
    if (!currentPrice) return "#E2E8F0";
    const t = savedPriceThreshold;
    if (currentPrice.price_per_mwh < t) return "#22C55E";
    if (currentPrice.price_per_mwh <= Math.max(t * 3, 500)) return "#FACC15";
    return "#EF4444";
  }, [currentPrice, savedPriceThreshold]);

  const authHeader = useMemo((): Record<string, string> => {
    if (!accessToken) return {};
    return { Authorization: `Bearer ${accessToken}` };
  }, [accessToken]);

  const logout = useCallback(async () => {
    if (accessToken) {
      try {
        await fetch(`${API_BASE}/auth/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      } catch {
        /* best-effort audit */
      }
    }
    clearStoredToken();
    setAccessToken(null);
    setUser(null);
    setAuthChecked(true);
    setAuthEmail("");
    setAuthPassword("");
    setAuthError(null);
    setAuthScreen("login");
    setError(null);
    setJobs([]);
    setCurrentPrice(null);
    setPriceHistory([]);
    setSavings(null);
    setLastOptimization(null);
    setEventLog([]);
    setFeedStatus(null);
    setLastUpdatedAt(null);
    setAuditEvents([]);
    setJobActionPending({});
    setJobActionErrors({});
    setJobActionSuccess({});
    setOnboardingOpen(false);
    setSetupGuideOpen(false);
    setAgentDetail(null);
    setAgentSetupOpen(false);
    setAgentSetupToken(null);
    if (agentVerifyTimerRef.current != null) {
      window.clearInterval(agentVerifyTimerRef.current);
      agentVerifyTimerRef.current = null;
    }
  }, [accessToken]);

  const fetchAuditLog = useCallback(async () => {
    if (!accessToken) return;
    setAuditLoading(true);
    try {
      const params = new URLSearchParams();
      if (auditFilter === "system") params.set("actor", "system");
      else if (auditFilter === "human") params.set("actor", "human");
      else if (auditFilter === "settings") params.set("preset", "settings");
      if (auditFromDate) params.set("from_date", auditFromDate);
      if (auditToDate) params.set("to_date", auditToDate);
      const qs = params.toString();
      const res = await fetch(`${API_BASE}/api/audit${qs ? `?${qs}` : ""}`, {
        headers: { ...authHeader },
      });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) throw new Error("Audit log unavailable");
      const data = (await res.json()) as { events: AuditLogItem[] };
      setAuditEvents(Array.isArray(data.events) ? data.events : []);
    } catch {
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, [accessToken, authHeader, auditFilter, auditFromDate, auditToDate, logout]);

  const fetchFeedStatus = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/feed/status`, { headers: { ...authHeader } });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) return;
      const data = (await res.json()) as FeedStatus;
      setFeedStatus(data);
    } catch {
      /* keep last known status */
    }
  }, [accessToken, authHeader, logout]);

  const fetchForecast = useCallback(async () => {
    if (!accessToken) return;
    try {
      const [fRes, hRes] = await Promise.all([
        fetch(`${API_BASE}/api/forecast`, { headers: { ...authHeader } }),
        fetch(`${API_BASE}/api/forecast/history`, { headers: { ...authHeader } }),
      ]);
      if (fRes.status === 401 || hRes.status === 401) {
        logout();
        return;
      }
      if (fRes.ok) setForecast((await fRes.json()) as ForecastResponse);
      if (hRes.ok) setForecastHistory((await hRes.json()) as ForecastHistoryItem[]);
    } catch {
      /* ignore transient forecast failure */
    }
  }, [accessToken, authHeader, logout]);

  const fetchBillingInvoices = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/billing/invoices`, { headers: { ...authHeader } });
      if (res.status === 401) {
        logout();
        return;
      }
      if (!res.ok) return;
      const payload = (await res.json()) as { invoices?: BillingInvoice[] };
      setBillingInvoices(Array.isArray(payload.invoices) ? payload.invoices : []);
    } catch {
      setBillingInvoices([]);
    }
  }, [accessToken, authHeader, logout]);

  const fetchDashboardData = useCallback(async () => {
    if (!accessToken) return;
    try {
      setError(null);
      const headers = { ...authHeader };
      const [jobsRes, priceRes, historyRes, savingsRes, statusRes, carbonRes] = await Promise.all([
        fetch(`${API_BASE}/api/jobs`, { headers }),
        fetch(`${API_BASE}/api/price/current`, { headers }),
        fetch(`${API_BASE}/api/price/history`, { headers }),
        fetch(`${API_BASE}/api/savings`, { headers }),
        fetch(`${API_BASE}/api/status`, { headers }),
        fetch(`${API_BASE}/api/carbon`, { headers }),
      ]);

      if (jobsRes.status === 401 || statusRes.status === 401) {
        logout();
        throw new Error("Session expired. Please sign in again.");
      }

      if (
        !jobsRes.ok ||
        !priceRes.ok ||
        !historyRes.ok ||
        !savingsRes.ok ||
        !statusRes.ok ||
        !carbonRes.ok
      ) {
        throw new Error("Backend API unavailable.");
      }

      const jobsData = await jobsRes.json();
      const currentPriceData = await priceRes.json();
      const historyData = await historyRes.json();
      const savingsData = await savingsRes.json();
      const statusData = await statusRes.json();
      const carbonData = (await carbonRes.json()) as CarbonSummaryResponse;

      setJobs(jobsData.jobs ?? []);
      const fetchedJobs: Job[] = jobsData.jobs ?? [];
      setJobPriorityOverrides((prev) => {
        const next: Record<string, JobPriority> = { ...prev };
        for (const job of fetchedJobs) {
          if (!next[job.id]) {
            next[job.id] = job.priority;
          }
        }
        return next;
      });
      setCurrentPrice(currentPriceData);
      setPriceHistory(Array.isArray(historyData) ? historyData : []);
      setSavings(savingsData);
      setCarbonSummary(carbonData);
      setLastOptimization(statusData.last_optimization_result ?? null);
      if (typeof statusData.price_threshold === "number") {
        setSavedPriceThreshold(statusData.price_threshold);
      }
      setEventLog(
        Array.isArray(statusData.optimization_events) ? statusData.optimization_events : []
      );
      const agentBlock = (statusData as { agent_status?: AgentStatusBlock }).agent_status;
      if (agentBlock && typeof agentBlock.token_exists === "boolean") {
        setAgentStatus({
          token_exists: agentBlock.token_exists,
          is_online: agentBlock.is_online,
          last_seen: agentBlock.last_seen ?? null,
          total_events: agentBlock.total_events,
        });
      }
      setLastUpdatedAt(new Date());
    } catch (fetchError) {
      const message =
        fetchError instanceof Error ? fetchError.message : "Failed to load dashboard data.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, authHeader, logout]);

  const fetchAgentDetail = useCallback(async () => {
    if (!accessToken) return null;
    setAgentDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/agent/detail`, { headers: { ...authHeader } });
      if (res.status === 401) {
        logout();
        return null;
      }
      if (!res.ok) return null;
      const data = (await res.json()) as AgentDetail;
      setAgentDetail(data);
      return data;
    } catch {
      return null;
    } finally {
      setAgentDetailLoading(false);
    }
  }, [accessToken, authHeader, logout]);

  const fetchSettings = useCallback(async () => {
    if (!accessToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/settings`, { headers: { ...authHeader } });
      if (response.status === 401) {
        logout();
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to load settings.");
      }
      const data = (await response.json()) as SettingsPayload;
      setSavedPriceThreshold(data.price_threshold);
      setPriceThreshold(data.price_threshold);
      setAutoOptimizationEnabled(data.auto_optimization_enabled);
      setMaxPauseDuration(data.max_pause_duration);
      setBlackoutStartHour(data.blackout_start_hour ?? null);
      setBlackoutEndHour(data.blackout_end_hour ?? null);
      setEmailAlertsEnabled(data.email_alerts_enabled);
      setSlackNotificationsEnabled(data.slack_notifications_enabled);
      setNotificationTarget(typeof data.notification_target === "string" ? data.notification_target : "");
      setNotificationEmail(typeof data.notification_email === "string" ? data.notification_email : "");
      setBillingContactEmail(
        typeof (data as SettingsPayload & { billing_contact_email?: string }).billing_contact_email === "string"
          ? ((data as SettingsPayload & { billing_contact_email?: string }).billing_contact_email as string)
          : ""
      );
      setFeePercentage(
        typeof (data as SettingsPayload & { fee_percentage?: number }).fee_percentage === "number"
          ? Number((data as SettingsPayload & { fee_percentage?: number }).fee_percentage)
          : 17.5
      );
      setWatchdogEnabled(data.watchdog_enabled);
      setWatchdogSensitivityPct(data.watchdog_sensitivity_pct);
      setWatchdogAbsoluteTrigger(data.watchdog_absolute_trigger);
      const ccp = (data as SettingsPayload & { carbon_credit_price?: number }).carbon_credit_price;
      setCarbonCreditPrice(typeof ccp === "number" && Number.isFinite(ccp) ? ccp : 20);
      const mr = normalizeMarketRegion(data.market_region);
      setSavedMarketRegion(mr);
      setMarketRegion(mr);
      if (data.job_priorities && typeof data.job_priorities === "object") {
        setJobPriorityOverrides(data.job_priorities as Record<string, JobPriority>);
      }
    } catch (settingsErr) {
      const message =
        settingsErr instanceof Error ? settingsErr.message : "Failed to load settings.";
      setError(message);
    }
  }, [accessToken, authHeader, logout]);

  useEffect(() => {
    const token = readStoredToken();
    if (!token) {
      setAuthChecked(true);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.status === 401 || res.status === 403) {
          clearStoredToken();
          setAccessToken(null);
          setUser(null);
          return;
        }
        if (!res.ok) {
          setAccessToken(token);
          return;
        }
        const me = (await res.json()) as UserInfo;
        setUser(me);
        setAccessToken(token);
      } catch {
        if (!cancelled) {
          setAccessToken(token);
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!accessToken) {
      setLoading(false);
      return;
    }
    setLoading(true);
    void fetchSettings();
    void fetchBillingInvoices();
    fetchDashboardData();
    void fetchForecast();
    void fetchAgentDetail();
    const intervalId = setInterval(() => {
      fetchDashboardData();
      void fetchAgentDetail();
    }, 30_000);
    return () => clearInterval(intervalId);
  }, [accessToken, fetchAgentDetail, fetchBillingInvoices, fetchDashboardData, fetchForecast, fetchSettings]);

  useEffect(() => {
    if (!accessToken) return;
    void fetchFeedStatus();
    const id = setInterval(() => void fetchFeedStatus(), 30_000);
    return () => clearInterval(id);
  }, [accessToken, fetchFeedStatus]);

  useEffect(() => {
    if (!accessToken) return;
    void fetchForecast();
    const id = setInterval(() => void fetchForecast(), 300_000);
    return () => clearInterval(id);
  }, [accessToken, fetchForecast]);

  useEffect(() => {
    if (!accessToken) return;
    void fetchAuditLog();
    const id = setInterval(() => void fetchAuditLog(), 300_000);
    return () => clearInterval(id);
  }, [accessToken, fetchAuditLog]);

  useEffect(() => {
    const pulseId = setInterval(() => setLivePulseOn((prev) => !prev), 700);
    return () => clearInterval(pulseId);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setClockMs(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    if (settingsOpen) {
      setPriceThreshold(savedPriceThreshold);
      setMarketRegion(savedMarketRegion);
    }
  }, [settingsOpen, savedPriceThreshold, savedMarketRegion]);

  useEffect(() => {
    if (!user || !accessToken) {
      setOnboardingOpen(false);
      return;
    }
    if (setupGuideOpen) {
      setOnboardingOpen(true);
      return;
    }
    const completed = Boolean(user.onboarding_completed);
    const needsAgent = !agentStatus.token_exists;
    setOnboardingOpen(!completed && needsAgent);
  }, [user, accessToken, agentStatus.token_exists, setupGuideOpen]);

  useEffect(() => {
    return () => {
      Object.values(jobActionSuccessTimers.current).forEach((timer) => window.clearTimeout(timer));
      jobActionSuccessTimers.current = {};
    };
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth > 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    setAuditPage(1);
  }, [auditFilter, auditFromDate, auditToDate, auditEvents.length]);

  useEffect(() => {
    if (!auditSectionRef.current) return;
    auditSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [auditPage]);

  useEffect(() => {
    if (!settingsOpen) {
      settingsWatchdogHydrated.current = false;
      return;
    }
    if (!feedStatus || settingsWatchdogHydrated.current) return;
    setWatchdogEnabled(feedStatus.watchdog_setting_enabled ?? true);
    setWatchdogSensitivityPct(feedStatus.watchdog_sensitivity_pct ?? 50);
    setWatchdogAbsoluteTrigger(feedStatus.watchdog_absolute_trigger_mwh ?? 500);
    settingsWatchdogHydrated.current = true;
  }, [settingsOpen, feedStatus]);

  const isDataStale = useMemo(() => {
    if (!lastUpdatedAt) return false;
    return clockMs - lastUpdatedAt.getTime() > 90_000;
  }, [clockMs, lastUpdatedAt]);

  const handleJobAction = async (jobId: string, action: "pause" | "resume") => {
    const prior = jobs.find((j) => j.id === jobId);
    const nextStatus: JobStatus = action === "pause" ? "PAUSED" : "RUNNING";
    const successText = action === "pause" ? "Paused ✓" : "Resumed ✓";
    setJobActionErrors((prev) => ({ ...prev, [jobId]: "" }));
    setJobActionPending((prev) => ({ ...prev, [jobId]: true }));
    setJobs((prev) =>
      prev.map((job) => (job.id === jobId ? { ...job, status: nextStatus } : job))
    );
    setJobActionSuccess((prev) => ({ ...prev, [jobId]: successText }));
    if (jobActionSuccessTimers.current[jobId] !== undefined) {
      window.clearTimeout(jobActionSuccessTimers.current[jobId]);
    }
    jobActionSuccessTimers.current[jobId] = window.setTimeout(() => {
      setJobActionSuccess((prev) => ({ ...prev, [jobId]: "" }));
      delete jobActionSuccessTimers.current[jobId];
    }, 1000);

    try {
      const res = await fetch(`${API_BASE}/api/jobs/${jobId}/${action}`, {
        method: "POST",
        headers: { ...authHeader },
      });
      if (res.status === 401) {
        void logout();
        return;
      }
      if (!res.ok) throw new Error("Request failed");
      setJobActionPending((prev) => ({ ...prev, [jobId]: false }));

      // Confirm with backend silently in the background.
      void (async () => {
        try {
          const jobsRes = await fetch(`${API_BASE}/api/jobs`, { headers: { ...authHeader } });
          if (jobsRes.status === 401) {
            void logout();
            return;
          }
          if (!jobsRes.ok) return;
          const jobsData = await jobsRes.json();
          const freshJobs: Job[] = jobsData.jobs ?? [];
          setJobs(freshJobs);
          setJobPriorityOverrides((prev) => {
            const next: Record<string, JobPriority> = { ...prev };
            for (const job of freshJobs) {
              if (!next[job.id]) next[job.id] = job.priority;
            }
            return next;
          });
        } catch {
          /* keep optimistic state if confirm fetch fails */
        }
      })();
    } catch {
      if (prior) {
        setJobs((prev) =>
          prev.map((job) => (job.id === jobId ? { ...job, status: prior.status } : job))
        );
      }
      setJobActionErrors((prev) => ({
        ...prev,
        [jobId]: `Failed to ${action} this job. Please retry.`,
      }));
      setJobActionSuccess((prev) => ({ ...prev, [jobId]: "" }));
      setJobActionPending((prev) => ({ ...prev, [jobId]: false }));
    }
  };

  const runOptimizationNow = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/optimize/run`, {
        method: "POST",
        headers: { ...authHeader },
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) {
        throw new Error("Optimization request failed.");
      }
      const result = await response.json();
      setLastOptimization(result);
      await fetchDashboardData();
    } catch (runError) {
      const message = runError instanceof Error ? runError.message : "Failed to run optimization.";
      setError(message);
    }
  };

  const simulateSpike = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/debug/spike`, {
        method: "POST",
        headers: { ...authHeader },
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to trigger simulated spike.");
      }
      // Give backend time to finish deterministic debug optimization + notifications.
      await new Promise<void>((resolve) => {
        window.setTimeout(() => resolve(), 2000);
      });
      await fetchDashboardData();
    } catch (spikeError) {
      const message =
        spikeError instanceof Error ? spikeError.message : "Failed to simulate spike.";
      setError(message);
    }
  };

  const updateJobClassification = (jobId: string, priority: JobPriority) => {
    setJobPriorityOverrides((prev) => ({
      ...prev,
      [jobId]: priority,
    }));
  };

  const buildSettingsPayload = (overrides?: Partial<SettingsPayload>): SettingsPayload => ({
    price_threshold: overrides?.price_threshold ?? priceThreshold,
    auto_optimization_enabled: overrides?.auto_optimization_enabled ?? autoOptimizationEnabled,
    max_pause_duration: overrides?.max_pause_duration ?? maxPauseDuration,
    blackout_start_hour: overrides?.blackout_start_hour ?? blackoutStartHour,
    blackout_end_hour: overrides?.blackout_end_hour ?? blackoutEndHour,
    email_alerts_enabled: overrides?.email_alerts_enabled ?? emailAlertsEnabled,
    slack_notifications_enabled: overrides?.slack_notifications_enabled ?? slackNotificationsEnabled,
    notification_target: (overrides?.notification_target ?? notificationTarget).trim(),
    notification_email: (overrides?.notification_email ?? notificationEmail).trim(),
    billing_contact_email: (overrides?.billing_contact_email ?? billingContactEmail).trim(),
    fee_percentage: overrides?.fee_percentage ?? feePercentage,
    job_priorities: overrides?.job_priorities ?? effectiveJobPriorities,
    watchdog_enabled: overrides?.watchdog_enabled ?? watchdogEnabled,
    watchdog_sensitivity_pct: overrides?.watchdog_sensitivity_pct ?? watchdogSensitivityPct,
    watchdog_absolute_trigger: overrides?.watchdog_absolute_trigger ?? watchdogAbsoluteTrigger,
    carbon_credit_price: overrides?.carbon_credit_price ?? carbonCreditPrice,
    market_region: normalizeMarketRegion(overrides?.market_region ?? marketRegion),
  });

  const sendSettingsPayload = async (payload: SettingsPayload) => {
    const response = await fetch(`${API_BASE}/api/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeader },
      body: JSON.stringify(payload),
    });
    if (response.status === 401) {
      logout();
      throw new Error("Session expired.");
    }
    if (!response.ok) {
      throw new Error("Failed to save settings.");
    }
    setSavedPriceThreshold(payload.price_threshold);
    setSavedMarketRegion(normalizeMarketRegion(payload.market_region));
    setError(null);
    await fetchDashboardData();
    await fetchFeedStatus();
  };

  const saveOnboardingSettings = async () => {
    await sendSettingsPayload(buildSettingsPayload());
  };

  const refreshJobsForOnboarding = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_BASE}/api/jobs`, { headers: { ...authHeader } });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs ?? []);
      }
    } catch {
      /* ignore */
    }
  }, [accessToken, authHeader]);

  const handleOnboardingComplete = useCallback(async () => {
    writeOnboardingComplete();
    if (accessToken) {
      try {
        const res = await fetch(`${API_BASE}/auth/onboarding/complete`, {
          method: "POST",
          headers: { ...authHeader },
        });
        if (res.ok) {
          const me = (await res.json()) as UserInfo;
          setUser(me);
        }
      } catch {
        /* local flag still set */
      }
    }
    setOnboardingOpen(false);
    setSetupGuideOpen(false);
    void fetchAgentDetail();
    void fetchDashboardData();
  }, [accessToken, authHeader, fetchAgentDetail, fetchDashboardData]);

  const sendSettingsTestNotification = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notifications/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ target: notificationTarget }),
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      const data = (await response.json().catch(() => ({}))) as { message?: string; detail?: unknown };
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(
          typeof detail === "string" ? detail : "Test notification failed."
        );
      }
      setSaveConfirmation(data.message ?? "Test notification sent.");
      setTimeout(() => setSaveConfirmation(null), 4000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test notification failed.");
    }
  };

  const sendSettingsTestEmail = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/notifications/test-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({ target: notificationEmail }),
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      const data = (await response.json().catch(() => ({}))) as { message?: string; detail?: unknown };
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === "string" ? detail : "Test email failed.");
      }
      setSaveConfirmation(data.message ?? "Test email sent.");
      setTimeout(() => setSaveConfirmation(null), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test email failed.");
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const generateAgentToken = async () => {
    if (!accessToken || !user) return;
    setAgentSetupBusy(true);
    try {
      const url = user.is_admin
        ? `${API_BASE}/v1/agent/token`
        : `${API_BASE}/api/agent/token`;
      const body = user.is_admin
        ? JSON.stringify({ customer_id: user.id, name: "dashboard-agent" })
        : JSON.stringify({ name: "dashboard-agent" });
      const res = await fetch(url, {
        method: "POST",
        headers: { ...authHeader, "Content-Type": "application/json" },
        body,
      });
      if (res.status === 401) {
        void logout();
        return;
      }
      if (!res.ok) throw new Error("Could not create agent token.");
      const data = (await res.json()) as { token: string };
      setAgentSetupToken(data.token);
      void fetchAgentDetail();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Token creation failed.");
    } finally {
      setAgentSetupBusy(false);
    }
  };

  const startAgentVerify = () => {
    if (agentVerifyTimerRef.current != null) {
      window.clearInterval(agentVerifyTimerRef.current);
    }
    setAgentVerifying(true);
    setAgentVerifyMessage("Waiting for agent heartbeat…");
    let attempts = 0;
    const maxAttempts = 12;
    agentVerifyTimerRef.current = window.setInterval(async () => {
      attempts += 1;
      const detail = await fetchAgentDetail();
      if (detail?.is_online) {
        setAgentVerifyMessage("Agent connected!");
        setAgentVerifying(false);
        if (agentVerifyTimerRef.current != null) {
          window.clearInterval(agentVerifyTimerRef.current);
          agentVerifyTimerRef.current = null;
        }
        return;
      }
      if (attempts >= maxAttempts) {
        setAgentVerifyMessage("No connection yet. Check install steps and try again.");
        setAgentVerifying(false);
        if (agentVerifyTimerRef.current != null) {
          window.clearInterval(agentVerifyTimerRef.current);
          agentVerifyTimerRef.current = null;
        }
      }
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (agentVerifyTimerRef.current != null) {
        window.clearInterval(agentVerifyTimerRef.current);
      }
    };
  }, []);

  const saveSettings = async () => {
    try {
      await sendSettingsPayload(buildSettingsPayload());
      setSaveConfirmation("Settings saved!");
      setTimeout(() => setSaveConfirmation(null), 2500);
    } catch (saveError) {
      const message = saveError instanceof Error ? saveError.message : "Failed to save settings.";
      setError(message);
    }
  };

  const downloadInvoicePdf = async (invoiceId: string) => {
    try {
      const response = await fetch(`${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}/pdf`, {
        headers: { ...authHeader },
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) throw new Error("Failed to download invoice PDF.");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to download invoice.");
    }
  };

  const downloadMonthlyReport = async () => {
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    setReportDownloading(true);
    try {
      const response = await fetch(`${API_BASE}/api/reports/monthly?month=${month}`, {
        headers: { ...authHeader },
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch monthly report data.");
      }

      const report = (await response.json()) as MonthlyReportResponse;
      const doc = new jsPDF({ unit: "pt", format: "a4" });
      const margin = 40;
      const pageW = doc.internal.pageSize.getWidth();

      const TEAL: [number, number, number] = [14, 165, 160];
      const headerH = 88;
      doc.setFillColor(...TEAL);
      doc.rect(0, 0, pageW, headerH, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.text("EloPlus", margin, 52);

      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text("Monthly Savings Report", margin, headerH + 36);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      let coverY = headerH + 58;
      doc.text(report.company_name, margin, coverY);
      coverY += 22;
      doc.setFontSize(11);
      doc.setTextColor(71, 85, 105);
      doc.text(`Billing period: ${report.billing_period_label}`, margin, coverY);
      coverY += 18;
      doc.text(`Invoice reference: ${report.invoice_reference}`, margin, coverY);
      coverY += 28;
      doc.setTextColor(30, 41, 59);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(14);
      doc.text(`Total savings: $${report.total_savings_usd.toFixed(2)}`, margin, coverY);
      coverY += 28;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(71, 85, 105);
      doc.text("Prepared by EloPlus Energy Intelligence", margin, coverY);
      coverY += 16;
      doc.text(
        `Report generated: ${new Date(report.generated_at).toLocaleString()}`,
        margin,
        coverY
      );
      coverY += 22;
      const feeNote =
        "This report documents automated energy optimization actions taken on your behalf. " +
        "Your fee is 15% of Total Savings shown above.";
      const feeLines = doc.splitTextToSize(feeNote, pageW - margin * 2);
      doc.text(feeLines, margin, coverY);

      doc.addPage();
      doc.setTextColor(0, 0, 0);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Executive Summary", margin, 44);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      const mc = report.month_carbon_credits ?? 0;
      const mv = report.month_carbon_value_usd ?? 0;
      const cc = report.cumulative_carbon_credits ?? 0;
      const cv = report.cumulative_carbon_value_usd ?? 0;
      const mtons = (report.month_co2_epa_spike_kg ?? 0) / 1000;
      const trees = report.trees_equivalent_annual ?? 0;
      const summaryRows = [
        ["Total Savings", `$${report.total_savings_usd.toFixed(2)}`],
        ["Spike Events Handled", String(report.total_spike_events)],
        ["CO2 Avoided", `${report.total_co2_avoided_kg.toFixed(2)} kg`],
        ["Carbon credits (month)", mc.toFixed(3)],
        ["Carbon credit value (month)", `$${mv.toFixed(2)}`],
        ["Cumulative carbon credits", cc.toFixed(3)],
        ["Cumulative carbon value", `$${cv.toFixed(2)}`],
        ["CO₂ avoided (EPA spikes, month)", `${mtons.toFixed(3)} metric tons`],
        ["Tree equivalency (illustrative)", `${trees.toFixed(1)} trees·year`],
        ["Average Market Price", `$${report.avg_market_price_per_mwh.toFixed(2)} /MWh`],
        [
          "Average Effective Price Paid",
          `$${report.avg_effective_price_paid_per_mwh.toFixed(2)} /MWh`,
        ],
      ];

      autoTable(doc, {
        startY: 56,
        margin: { left: margin, right: margin },
        head: [["Metric", "Value"]],
        body: summaryRows,
        styles: { fontSize: 10, cellPadding: 6 },
        headStyles: { fillColor: [15, 94, 93] },
      });

      let nextY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? 170;
      nextY += 24;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Top 5 Most Expensive Events Handled", margin, nextY);

      autoTable(doc, {
        startY: nextY + 10,
        margin: { left: margin, right: margin },
        head: [["Timestamp", "Market Price", "Savings", "kW Saved", "Actions"]],
        body: report.top_expensive_events_handled.map((evt) => [
          new Date(evt.timestamp).toLocaleString(),
          `$${evt.price_per_mwh.toFixed(2)}`,
          `$${evt.estimated_savings_usd.toFixed(2)}`,
          evt.kw_saved.toFixed(2),
          evt.actions_taken.join(" | "),
        ]),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [30, 64, 175] },
      });

      nextY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? nextY + 40;
      nextY += 24;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Daily Savings Breakdown", margin, nextY);

      autoTable(doc, {
        startY: nextY + 10,
        margin: { left: margin, right: margin },
        head: [["Date", "Savings (USD)"]],
        body: report.daily_savings_breakdown.map((row) => [row.date, `$${row.savings_usd.toFixed(2)}`]),
        styles: { fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [22, 163, 74] },
      });

      nextY = (doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? nextY + 40;
      nextY += 24;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.text("Optimization Event Log", margin, nextY);

      const omitted = report.routine_monitoring_checks_omitted;
      const logNote =
        omitted === 1
          ? "Showing significant events only. 1 routine monitoring check omitted."
          : `Showing significant events only. ${omitted} routine monitoring checks omitted.`;
      doc.setFont("helvetica", "italic");
      doc.setFontSize(9);
      doc.setTextColor(71, 85, 105);
      doc.text(logNote, margin, nextY + 14);
      doc.setTextColor(0, 0, 0);

      autoTable(doc, {
        startY: nextY + 26,
        margin: { left: margin, right: margin },
        head: [["Timestamp", "Price", "Savings", "Spike", "Actions"]],
        body: report.event_log.map((evt) => [
          new Date(evt.timestamp).toLocaleString(),
          `$${evt.price_per_mwh.toFixed(2)}`,
          `$${evt.estimated_savings_usd.toFixed(2)}`,
          evt.is_spike ? "Yes" : "No",
          evt.actions_taken.join(" | "),
        ]),
        styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
        headStyles: { fillColor: [51, 65, 85] },
      });

      const monthDate = new Date(`${report.month}-01T00:00:00`);
      const fileMonth = monthDate
        .toLocaleDateString(undefined, { month: "long", year: "numeric" })
        .replace(" ", "_");
      doc.save(`EloPlus_Report_${fileMonth}.pdf`);
    } catch (reportErr) {
      const message =
        reportErr instanceof Error ? reportErr.message : "Failed to generate monthly report.";
      setError(message);
    } finally {
      setReportDownloading(false);
    }
  };

  const onBacktestRegionChange = (region: BacktestRegion) => {
    setBacktestRegion(region);
    setBacktestThreshold(BACKTEST_DEFAULT_THRESHOLDS[region]);
  };

  const runBacktest = async () => {
    setBacktestRunning(true);
    setBacktestError(null);
    try {
      const response = await fetch(`${API_BASE}/api/backtest/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          region: backtestRegion,
          cluster_mw: backtestClusterMw,
          threshold_price: backtestThreshold,
          months_back: backtestMonths,
        }),
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || "Backtest run failed.");
      }
      const result = (await response.json()) as BacktestResult;
      setBacktestResult(result);
      setBacktestModalOpen(false);
    } catch (runErr) {
      const message = runErr instanceof Error ? runErr.message : "Backtest run failed.";
      setBacktestError(message);
    } finally {
      setBacktestRunning(false);
    }
  };

  const runDryRun = async () => {
    setDryRunRunning(true);
    setDryRunError(null);
    try {
      const response = await fetch(`${API_BASE}/api/dryrun/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          month: dryRunMonth,
          cluster_mw: dryRunClusterMw,
          threshold: dryRunThreshold,
        }),
      });
      if (response.status === 401) {
        void logout();
        return;
      }
      if (!response.ok) {
        const detail = await response.json().catch(() => null);
        throw new Error(detail?.detail || "Dry-run simulation failed.");
      }
      const result = (await response.json()) as DryRunResult;
      setDryRunResult(result);
      setDryRunModalOpen(false);
    } catch (runErr) {
      const message = runErr instanceof Error ? runErr.message : "Dry-run simulation failed.";
      setDryRunError(message);
    } finally {
      setDryRunRunning(false);
    }
  };

  const formatHour = (hour: number) => {
    const normalized = ((hour % 24) + 24) % 24;
    const suffix = normalized >= 12 ? "PM" : "AM";
    const hour12 = normalized % 12 === 0 ? 12 : normalized % 12;
    return `${hour12}:00 ${suffix}`;
  };

  const chartData = useMemo(() => {
    // Downsample to 15-minute windows to keep chart rendering fast.
    const bucketMs = 15 * 60 * 1000;
    const buckets = new Map<
      number,
      { sum: number; count: number; timestamp: string; is_spike: boolean; carbon_sum: number }
    >();

    for (const point of priceHistory) {
      const ts = new Date(point.timestamp);
      const ms = ts.getTime();
      if (Number.isNaN(ms)) continue;
      const bucketStartMs = Math.floor(ms / bucketMs) * bucketMs;
      const existing = buckets.get(bucketStartMs);
      if (existing) {
        existing.sum += point.price_per_mwh;
        existing.count += 1;
        existing.is_spike = existing.is_spike || point.is_spike;
        existing.carbon_sum += point.carbon_intensity;
      } else {
        buckets.set(bucketStartMs, {
          sum: point.price_per_mwh,
          count: 1,
          timestamp: new Date(bucketStartMs).toISOString(),
          is_spike: point.is_spike,
          carbon_sum: point.carbon_intensity,
        });
      }
    }

    return Array.from(buckets.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([, bucket]) => ({
        price_per_mwh: bucket.sum / bucket.count,
        timestamp: bucket.timestamp,
        is_spike: bucket.is_spike,
        carbon_intensity: bucket.carbon_sum / bucket.count,
        label: new Date(bucket.timestamp).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      }));
  }, [priceHistory]);

  const formatAuthApiError = (data: unknown): string => {
    if (!data || typeof data !== "object") return "Request failed";
    const detail = (data as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
      return detail
        .map((entry) => (typeof entry === "object" && entry && "msg" in entry ? String((entry as { msg: string }).msg) : JSON.stringify(entry)))
        .join(" ");
    }
    return "Request failed";
  };

  useEffect(() => {
    if (location.pathname === "/reset-password") {
      setAuthScreen("reset");
    } else if (location.pathname === "/forgot-password") {
      setAuthScreen("forgot");
    } else if (location.pathname === "/request-access") {
      setAuthScreen("request-access");
    } else if (!accessToken) {
      setAuthScreen("login");
    }
  }, [location.pathname, accessToken]);

  useEffect(() => {
    if (authScreen !== "reset" || !resetTokenParam) {
      setResetTokenValid(resetTokenParam ? false : null);
      return;
    }
    let cancelled = false;
    setResetTokenChecking(true);
    setResetTokenValid(null);
    setResetError(null);
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/verify-reset-token?token=${encodeURIComponent(resetTokenParam)}`
        );
        const data = (await res.json().catch(() => ({}))) as { valid?: boolean };
        if (!cancelled) {
          setResetTokenValid(Boolean(data.valid));
        }
      } catch {
        if (!cancelled) {
          setResetTokenValid(false);
        }
      } finally {
        if (!cancelled) {
          setResetTokenChecking(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authScreen, resetTokenParam]);

  const submitForgotPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setForgotSubmitting(true);
    setForgotSuccessEmail(null);
    setForgotError(null);
    try {
      const res = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });
      if (!res.ok) {
        throw new Error("Could not reach the server. Check your connection and try again.");
      }
      setForgotSuccessEmail(forgotEmail.trim());
    } catch (err) {
      setForgotError(
        err instanceof Error
          ? err.message
          : "Could not reach the server. Check your connection and try again."
      );
    } finally {
      setForgotSubmitting(false);
    }
  };

  const submitResetPassword = async (event: React.FormEvent) => {
    event.preventDefault();
    setResetError(null);
    if (resetPassword.length < 8) {
      setResetError("Password must be at least 8 characters.");
      return;
    }
    if (!/[A-Za-z]/.test(resetPassword)) {
      setResetError("Password must contain at least one letter.");
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      setResetError("Passwords do not match.");
      return;
    }
    setResetSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: resetTokenParam, new_password: resetPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = (data as { detail?: string }).detail;
        const message =
          detail === "Invalid or expired reset link"
            ? detail
            : "Something went wrong. Please request a new reset link.";
        throw new Error(message);
      }
      setResetSuccess(true);
      setResetPassword("");
      setResetPasswordConfirm("");
      clearStoredToken();
      setAccessToken(null);
      setUser(null);
      navigate("/", { replace: true });
    } catch (err) {
      setResetError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please request a new reset link."
      );
    } finally {
      setResetSubmitting(false);
    }
  };

  const goToLogin = () => {
    setAuthScreen("login");
    setAuthError(null);
    setForgotSuccessEmail(null);
    setForgotError(null);
    setResetSuccess(false);
    setResetError(null);
    setRequestSuccess(false);
    setRequestError(null);
    if (location.pathname !== "/") {
      navigate("/", { replace: true });
    }
  };

  const goToRequestAccess = () => {
    setAuthScreen("request-access");
    setRequestSuccess(false);
    setRequestError(null);
    navigate("/request-access");
  };

  const authShellStyle: React.CSSProperties = {
    minHeight: "100vh",
    backgroundColor: "#0A1628",
    color: "#E2E8F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
  };

  const authCardStyle: React.CSSProperties = {
    ...cardStyle,
    width: "min(420px, 100%)",
    display: "grid",
    gap: 16,
  };

  const submitAuth = async (event: React.FormEvent) => {
    event.preventDefault();
    setAuthError(null);
    setAuthSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: authEmail.trim(), password: authPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatAuthApiError(data));
      }
      const token = (data as { access_token?: unknown }).access_token;
      if (typeof token !== "string" || !token) {
        throw new Error("Invalid response from server.");
      }
      writeStoredToken(token);
      setAccessToken(token);
      setUser((data as { user: UserInfo }).user);
      setAuthPassword("");
      setAuthChecked(true);
    } catch (err) {
      setAuthError(err instanceof Error ? err.message : "Authentication failed.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  const submitAccessRequest = async (event: React.FormEvent) => {
    event.preventDefault();
    setRequestError(null);
    setRequestSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/access/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: requestEmail.trim(),
          company_name: requestCompany.trim(),
          use_case: requestUseCase,
          estimated_gpu_count: requestGpuCount,
          region: requestRegion,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(formatAuthApiError(data));
      }
      setRequestSuccess(true);
    } catch (err) {
      setRequestError(err instanceof Error ? err.message : "Could not submit request.");
    } finally {
      setRequestSubmitting(false);
    }
  };

  const showAuthFlow =
    !accessToken ||
    location.pathname === "/reset-password" ||
    location.pathname === "/forgot-password" ||
    location.pathname === "/request-access" ||
    authScreen === "forgot" ||
    authScreen === "reset" ||
    authScreen === "request-access";

  if (!authChecked && readStoredToken()) {
    return (
      <div style={authShellStyle}>
        <div style={{ ...authCardStyle, textAlign: "center" }}>
          <p style={{ margin: 0, color: "#94A3B8" }}>Restoring your session…</p>
        </div>
      </div>
    );
  }

  if (showAuthFlow) {
    if (authScreen === "forgot") {
      return (
        <div style={authShellStyle}>
          <div style={authCardStyle}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, color: "#F8FAFC" }}>Reset your password</h1>
              <p style={{ margin: "8px 0 0", color: "#94A3B8", fontSize: 14 }}>
                Enter your email and we&apos;ll send you a reset link.
              </p>
            </div>
            {forgotSuccessEmail ? (
              <div style={{ display: "grid", gap: 14 }}>
                <p style={{ margin: 0, color: "#CBD5E1", fontSize: 14, lineHeight: 1.5 }}>
                  Check your email — we sent a reset link to{" "}
                  <strong style={{ color: "#F8FAFC" }}>{forgotSuccessEmail}</strong>
                </p>
                <button type="button" onClick={goToLogin} style={authLinkStyle}>
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={submitForgotPassword} style={{ display: "grid", gap: 12 }}>
                {forgotError && (
                  <div
                    style={{
                      color: "#FCA5A5",
                      fontSize: 13,
                      background: "rgba(127,29,29,0.35)",
                      padding: 10,
                      borderRadius: 8,
                    }}
                  >
                    {forgotError}
                  </div>
                )}
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Email
                  <input
                    type="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    style={authInputStyle}
                  />
                </label>
                <button
                  type="submit"
                  disabled={forgotSubmitting}
                  style={{
                    marginTop: 4,
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#0EA5A0",
                    color: "#042F2E",
                    fontWeight: 800,
                    cursor: forgotSubmitting ? "wait" : "pointer",
                    opacity: forgotSubmitting ? 0.75 : 1,
                  }}
                >
                  {forgotSubmitting ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" onClick={goToLogin} style={{ ...authLinkStyle, textAlign: "left" }}>
                  Back to login
                </button>
              </form>
            )}
          </div>
        </div>
      );
    }

    if (authScreen === "reset") {
      const invalidLink =
        !resetTokenParam ||
        (!resetTokenChecking && resetTokenValid === false);
      return (
        <div style={authShellStyle}>
          <div style={authCardStyle}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, color: "#F8FAFC" }}>Set a new password</h1>
            </div>
            {resetTokenChecking ? (
              <p style={{ margin: 0, color: "#94A3B8", fontSize: 14 }}>Verifying reset link…</p>
            ) : resetSuccess ? (
              <div style={{ display: "grid", gap: 14 }}>
                <p style={{ margin: 0, color: "#CBD5E1", fontSize: 14, lineHeight: 1.5 }}>
                  Password reset successfully. You can now sign in.
                </p>
                <button type="button" onClick={goToLogin} style={authLinkStyle}>
                  Back to login
                </button>
              </div>
            ) : invalidLink ? (
              <div style={{ display: "grid", gap: 14 }}>
                <p style={{ margin: 0, color: "#FCA5A5", fontSize: 14, lineHeight: 1.5 }}>
                  This reset link is invalid or has expired. Request a new one.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAuthScreen("forgot");
                    navigate("/", { replace: true });
                  }}
                  style={authLinkStyle}
                >
                  Request a new reset link
                </button>
              </div>
            ) : (
              <form onSubmit={submitResetPassword} style={{ display: "grid", gap: 12 }}>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  New password
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={authInputStyle}
                  />
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Confirm password
                  <input
                    type="password"
                    required
                    minLength={8}
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    style={authInputStyle}
                  />
                </label>
                {resetError && (
                  <div
                    style={{
                      color: "#FCA5A5",
                      fontSize: 13,
                      background: "rgba(127,29,29,0.35)",
                      padding: 10,
                      borderRadius: 8,
                    }}
                  >
                    {resetError}
                  </div>
                )}
                <button
                  type="submit"
                  disabled={resetSubmitting}
                  style={{
                    marginTop: 4,
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#0EA5A0",
                    color: "#042F2E",
                    fontWeight: 800,
                    cursor: resetSubmitting ? "wait" : "pointer",
                    opacity: resetSubmitting ? 0.75 : 1,
                  }}
                >
                  {resetSubmitting ? "Saving…" : "Reset password"}
                </button>
              </form>
            )}
          </div>
        </div>
      );
    }

    if (authScreen === "request-access") {
      return (
        <div style={authShellStyle}>
          <div style={{ ...authCardStyle, width: "min(480px, 100%)" }}>
            <div>
              <h1 style={{ margin: 0, fontSize: 24, color: "#F8FAFC" }}>Request Access</h1>
              <p style={{ margin: "8px 0 0", color: "#94A3B8", fontSize: 14, lineHeight: 1.5 }}>
                EloPlus is currently invite-only. Tell us about your infrastructure and we&apos;ll be
                in touch within 24 hours.
              </p>
            </div>
            {requestSuccess ? (
              <div style={{ display: "grid", gap: 14 }}>
                <p style={{ margin: 0, color: "#CBD5E1", fontSize: 14, lineHeight: 1.5 }}>
                  Request received. We&apos;ll be in touch within 24 hours.
                </p>
                <button type="button" onClick={goToLogin} style={authLinkStyle}>
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={submitAccessRequest} style={{ display: "grid", gap: 12 }}>
                {requestError && (
                  <div
                    style={{
                      color: "#FCA5A5",
                      fontSize: 13,
                      background: "rgba(127,29,29,0.35)",
                      padding: 10,
                      borderRadius: 8,
                    }}
                  >
                    {requestError}
                  </div>
                )}
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Work email
                  <input
                    type="email"
                    required
                    value={requestEmail}
                    onChange={(e) => setRequestEmail(e.target.value)}
                    style={authInputStyle}
                  />
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Company name
                  <input
                    type="text"
                    required
                    value={requestCompany}
                    onChange={(e) => setRequestCompany(e.target.value)}
                    style={authInputStyle}
                  />
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  What are you running?
                  <select
                    required
                    value={requestUseCase}
                    onChange={(e) => setRequestUseCase(e.target.value)}
                    style={authInputStyle}
                  >
                    <option value="GPU Cloud">GPU Cloud</option>
                    <option value="AI Research Lab">AI Research Lab</option>
                    <option value="Enterprise AI">Enterprise AI</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Estimated GPU count
                  <select
                    required
                    value={requestGpuCount}
                    onChange={(e) => setRequestGpuCount(e.target.value)}
                    style={authInputStyle}
                  >
                    <option value="1-50">1-50</option>
                    <option value="51-500">51-500</option>
                    <option value="500-5,000">500-5,000</option>
                    <option value="5,000+">5,000+</option>
                  </select>
                </label>
                <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
                  Grid region
                  <select
                    required
                    value={requestRegion}
                    onChange={(e) => setRequestRegion(e.target.value)}
                    style={authInputStyle}
                  >
                    <option value="ERCOT">ERCOT (Texas)</option>
                    <option value="PJM">PJM (Mid-Atlantic)</option>
                    <option value="CAISO">CAISO (California)</option>
                    <option value="Other">Other</option>
                  </select>
                </label>
                <button
                  type="submit"
                  disabled={requestSubmitting}
                  style={{
                    marginTop: 4,
                    padding: "12px 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#0EA5A0",
                    color: "#042F2E",
                    fontWeight: 800,
                    cursor: requestSubmitting ? "wait" : "pointer",
                    opacity: requestSubmitting ? 0.75 : 1,
                  }}
                >
                  {requestSubmitting ? "Submitting…" : "Request Access"}
                </button>
                <button type="button" onClick={goToLogin} style={{ ...authLinkStyle, textAlign: "left" }}>
                  Back to login
                </button>
              </form>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={authShellStyle}>
        <div style={authCardStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: 26, color: "#F8FAFC" }}>EloPlus</h1>
            <p style={{ margin: "8px 0 0", color: "#94A3B8", fontSize: 14 }}>
              AI Demand Response for data centers
            </p>
          </div>
          <form onSubmit={submitAuth} style={{ display: "grid", gap: 12 }}>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
              Email
              <input
                type="email"
                required
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
                style={authInputStyle}
              />
            </label>
            <label style={{ display: "grid", gap: 6, fontSize: 13, color: "#CBD5E1" }}>
              Password
              <input
                type="password"
                required
                minLength={8}
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                style={authInputStyle}
              />
              <button
                type="button"
                onClick={() => {
                  setAuthScreen("forgot");
                  setForgotEmail(authEmail);
                  setForgotSuccessEmail(null);
                  setForgotError(null);
                  navigate("/forgot-password");
                }}
                style={{ ...authLinkStyle, marginTop: 4, textAlign: "left" }}
              >
                Forgot password?
              </button>
            </label>
            {authError && (
              <div style={{ color: "#FCA5A5", fontSize: 13, background: "rgba(127,29,29,0.35)", padding: 10, borderRadius: 8 }}>
                {authError}
              </div>
            )}
            <button
              type="submit"
              disabled={authSubmitting}
              style={{
                marginTop: 4,
                padding: "12px 16px",
                borderRadius: 10,
                border: "none",
                background: "#0EA5A0",
                color: "#042F2E",
                fontWeight: 800,
                cursor: authSubmitting ? "wait" : "pointer",
                opacity: authSubmitting ? 0.75 : 1,
              }}
            >
              Sign in
            </button>
            <p style={{ margin: "4px 0 0", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>
              Want access?{" "}
              <button type="button" onClick={goToRequestAccess} style={authLinkStyle}>
                Request it →
              </button>
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div
      className="app-shell"
      style={{
        minHeight: "100vh",
        backgroundColor: "#0A1628",
        color: "#E2E8F0",
        padding: 24,
        fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      }}
    >
      <div className="dashboard-container" style={{ maxWidth: 1400, margin: "0 auto", display: "grid", gap: 18 }}>
        <div
          className="topbar"
          style={{
            ...cardStyle,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div className="topbar-main" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 14,
                height: 14,
                borderRadius: "50%",
                background: "#0EA5A0",
                opacity: livePulseOn ? 1 : 0.35,
                transition: "opacity 250ms ease-in-out",
                boxShadow: "0 0 14px #0EA5A0",
              }}
            />
            <h1 style={{ margin: 0, fontSize: 28, color: "#F8FAFC", letterSpacing: 0.5 }}>
              EloPlus
            </h1>
            <span style={{ color: "#67E8F9", fontSize: 13, fontWeight: 700 }}>LIVE</span>
            <span style={{ color: isDataStale ? "#FBBF24" : "#94A3B8", fontSize: 12 }}>
              Last updated: {formatLastUpdated(lastUpdatedAt)}
              {isDataStale ? " (stale)" : ""}
            </span>
            <div className="topbar-badges" style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 12 }}>
              <DataFeedIndicator status={feedStatus} />
              <WatchdogIndicator status={feedStatus} />
            </div>
          </div>

          <div className="topbar-actions" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            <div className="desktop-actions" style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap" }}>
            {user && (
              <div className="account-block" style={{ textAlign: "right", marginRight: 8, display: "grid", justifyItems: "end", gap: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#F8FAFC" }}>{user.company_name}</div>
                <div style={{ fontSize: 12, color: "#94A3B8" }}>{user.email}</div>
                {user.is_admin && (
                  <span
                    style={{
                      display: "inline-block",
                      borderRadius: 999,
                      border: "1px solid #0EA5A0",
                      background: "rgba(14,165,160,0.18)",
                      color: "#99F6E4",
                      fontSize: 10,
                      fontWeight: 800,
                      letterSpacing: 0.4,
                      padding: "2px 8px",
                    }}
                  >
                    ADMIN MODE
                  </span>
                )}
              </div>
            )}
            {user?.is_admin && (
              <Link
                to="/admin"
                style={{
                  background: "#0F172A",
                  color: "#67E8F9",
                  border: "1px solid #0EA5A0",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                Admin
              </Link>
            )}
            <button
              onClick={() => setSettingsOpen(true)}
              type="button"
              style={{
                background: "#1E293B",
                color: "#E2E8F0",
                border: "1px solid #334155",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              ⚙️ Settings
            </button>
            <button
              type="button"
              onClick={logout}
              style={{
                background: "transparent",
                color: "#94A3B8",
                border: "1px solid #334155",
                borderRadius: 10,
                padding: "10px 14px",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Log out
            </button>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4 }}>Current Grid Price</div>
              <div style={{ fontSize: 34, fontWeight: 800, color: priceColor }}>
                ${currentPrice?.price_per_mwh.toFixed(2) ?? "--"} /MWh
              </div>
            </div>
            {currentPrice?.is_spike && (
              <span
                style={{
                  background: "#EF4444",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 12,
                  borderRadius: 999,
                  padding: "8px 12px",
                  letterSpacing: 0.5,
                }}
              >
                SPIKE ALERT
              </span>
            )}
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 4 }}>Total Savings</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: "#22C55E" }}>
                ${savings?.total_cumulative_savings_usd.toFixed(2) ?? "0.00"}
              </div>
            </div>
            </div>
            <button
              className="mobile-menu-toggle"
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Open menu"
            >
              ☰
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="mobile-menu-panel">
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                setSettingsOpen(true);
              }}
            >
              Settings
            </button>
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                void logout();
              }}
            >
              Log out
            </button>
          </div>
        )}

        <OnboardingWizard
          open={onboardingOpen}
          apiBase={API_BASE}
          authHeader={authHeader}
          marketRegion={savedMarketRegion}
          priceThreshold={priceThreshold}
          setPriceThreshold={setPriceThreshold}
          jobs={jobs}
          effectiveJobPriorities={effectiveJobPriorities}
          currentPriceMwh={currentPrice?.price_per_mwh ?? null}
          onSaveSettings={saveOnboardingSettings}
          onUpdateJobPriority={updateJobClassification}
          onRefreshJobs={refreshJobsForOnboarding}
          fetchAgentDetail={fetchAgentDetail}
          onComplete={handleOnboardingComplete}
        />

        {settingsOpen && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(2, 6, 23, 0.75)",
              zIndex: 50,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: 20,
            }}
            onClick={() => setSettingsOpen(false)}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(1000px, 100%)",
                maxHeight: "90vh",
                overflowY: "auto",
                background: "#0F1E35",
                border: "1px solid #1F355B",
                borderRadius: 16,
                padding: 20,
                display: "grid",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h2 style={{ margin: 0, color: "#F8FAFC" }}>Platform Settings</h2>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSettingsOpen(false);
                      setSetupGuideOpen(true);
                      setOnboardingOpen(true);
                    }}
                    style={{
                      background: "transparent",
                      color: "#67E8F9",
                      border: "1px solid #0EA5A0",
                      borderRadius: 8,
                      padding: "8px 12px",
                      fontWeight: 700,
                      cursor: "pointer",
                      fontSize: 13,
                    }}
                  >
                    Setup Guide
                  </button>
                  <button
                    type="button"
                    onClick={() => setSettingsOpen(false)}
                    style={{
                      background: "transparent",
                      color: "#94A3B8",
                      border: "none",
                      fontSize: 20,
                      cursor: "pointer",
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div style={{ ...cardStyle, background: "#13213A", margin: 0 }}>
                <h3 style={{ margin: "0 0 12px", color: "#E2E8F0", fontSize: 16 }}>
                  Optimization Settings
                </h3>
                <div style={{ display: "grid", gap: 12 }}>
                  <div>
                    <label style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 700 }}>Grid region</label>
                    <select
                      value={marketRegion}
                      onChange={(event) => {
                        const r = normalizeMarketRegion(event.target.value);
                        setMarketRegion(r);
                        setPriceThreshold(DEFAULT_SPIKE_THRESHOLD_BY_REGION[r]);
                      }}
                      style={{
                        marginTop: 6,
                        width: "100%",
                        maxWidth: 420,
                        background: "#0B1220",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 10px",
                        fontWeight: 600,
                      }}
                    >
                      <option value="ERCOT">ERCOT (Texas)</option>
                      <option value="PJM">PJM (Mid-Atlantic &amp; Midwest)</option>
                      <option value="CAISO">CAISO (California)</option>
                    </select>
                    <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 6 }}>
                      Changing region switches the live price feed and pre-fills the suggested pause threshold ($
                      {DEFAULT_SPIKE_THRESHOLD_BY_REGION[marketRegion]}/MWh for {marketRegion}).
                    </div>
                  </div>
                  <div>
                    <label style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 700 }}>
                      Price Threshold: ${priceThreshold}/MWh
                    </label>
                    <input
                      type="range"
                      min={50}
                      max={500}
                      value={priceThreshold}
                      onChange={(event) => setPriceThreshold(Number(event.target.value))}
                      style={{ width: "100%", marginTop: 8, accentColor: "#0EA5A0" }}
                    />
                    <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 6 }}>
                      Currently: pause flexible jobs above ${priceThreshold}/MWh
                    </div>
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#CBD5E1" }}>
                    <input
                      type="checkbox"
                      checked={autoOptimizationEnabled}
                      onChange={(event) => setAutoOptimizationEnabled(event.target.checked)}
                    />
                    Auto-optimization on/off (runs every 5 min)
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      color: "#CBD5E1",
                      opacity: autoOptimizationEnabled ? 1 : 0.45,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={watchdogEnabled}
                      disabled={!autoOptimizationEnabled}
                      onChange={(event) => setWatchdogEnabled(event.target.checked)}
                    />
                    Spike Watchdog — 30s emergency detection
                  </label>
                  <div style={{ opacity: autoOptimizationEnabled && watchdogEnabled ? 1 : 0.45 }}>
                    <label style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 700 }}>
                      Trigger if price jumps more than {watchdogSensitivityPct}%
                    </label>
                    <input
                      type="range"
                      min={25}
                      max={100}
                      step={5}
                      value={watchdogSensitivityPct}
                      disabled={!autoOptimizationEnabled || !watchdogEnabled}
                      onChange={(event) => setWatchdogSensitivityPct(Number(event.target.value))}
                      style={{ width: "100%", marginTop: 8, accentColor: "#0EA5A0" }}
                    />
                    <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 6 }}>
                      Currently: trigger if price jumps more than {watchdogSensitivityPct}% in 30 seconds
                    </div>
                  </div>
                  <div style={{ opacity: autoOptimizationEnabled && watchdogEnabled ? 1 : 0.45 }}>
                    <label style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 700 }}>
                      Always trigger above $/MWh
                    </label>
                    <input
                      type="number"
                      min={100}
                      max={5000}
                      step={10}
                      value={watchdogAbsoluteTrigger}
                      disabled={!autoOptimizationEnabled || !watchdogEnabled}
                      onChange={(event) =>
                        setWatchdogAbsoluteTrigger(Number(event.target.value) || 100)
                      }
                      style={{
                        marginTop: 8,
                        width: "100%",
                        maxWidth: 200,
                        background: "#0B1220",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 10px",
                      }}
                    />
                    <div style={{ color: "#94A3B8", fontSize: 12, marginTop: 6 }}>
                      Regardless of % jump, always act above this price.
                    </div>
                  </div>
                  <div>
                    <label style={{ color: "#CBD5E1", fontSize: 13, fontWeight: 700 }}>
                      Max pause duration
                    </label>
                    <select
                      value={maxPauseDuration}
                      onChange={(event) => setMaxPauseDuration(event.target.value)}
                      style={{
                        marginTop: 6,
                        width: "100%",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 10px",
                      }}
                    >
                      <option value="1hr">1hr</option>
                      <option value="2hr">2hr</option>
                      <option value="4hr">4hr</option>
                      <option value="8hr">8hr</option>
                      <option value="unlimited">unlimited</option>
                    </select>
                  </div>
                </div>
              </div>

              <div style={{ ...cardStyle, background: "#13213A", margin: 0 }}>
                <h3 style={{ margin: "0 0 12px", color: "#E2E8F0", fontSize: 16 }}>
                  Job Classification
                </h3>
                <div style={{ color: "#FCA5A5", fontSize: 12, marginBottom: 10 }}>
                  Critical jobs are never paused by the optimizer.
                </div>
                <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid #1F355B", borderRadius: 10 }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "#94A3B8", textAlign: "left", background: "#0B1220" }}>
                        <th style={{ padding: "8px 10px" }}>Job</th>
                        <th style={{ padding: "8px 10px" }}>Priority</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((job) => (
                        <tr key={`settings-${job.id}`} style={{ borderTop: "1px solid #1F355B" }}>
                          <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>{job.name}</td>
                          <td style={{ padding: "8px 10px" }}>
                            <select
                              value={effectiveJobPriorities[job.id] ?? job.priority}
                              onChange={(event) =>
                                updateJobClassification(job.id, event.target.value as JobPriority)
                              }
                              style={{
                                background: "#0B1220",
                                color: "#E2E8F0",
                                border: "1px solid #334155",
                                borderRadius: 8,
                                padding: "6px 8px",
                              }}
                            >
                              <option value="CRITICAL">CRITICAL</option>
                              <option value="FLEXIBLE">FLEXIBLE</option>
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ ...cardStyle, background: "#13213A", margin: 0 }}>
                <h3 style={{ margin: "0 0 12px", color: "#E2E8F0", fontSize: 16 }}>Blackout Windows</h3>
                <div style={{ color: "#94A3B8", fontSize: 12, marginBottom: 10 }}>
                  Never optimize during these hours.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ color: "#CBD5E1", fontSize: 13 }}>Start hour</label>
                    <select
                      value={blackoutStartHour ?? 9}
                      onChange={(event) => setBlackoutStartHour(Number(event.target.value))}
                      style={{
                        marginTop: 6,
                        width: "100%",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 10px",
                      }}
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <option key={`start-${hour}`} value={hour}>
                          {formatHour(hour)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ color: "#CBD5E1", fontSize: 13 }}>End hour</label>
                    <select
                      value={blackoutEndHour ?? 17}
                      onChange={(event) => setBlackoutEndHour(Number(event.target.value))}
                      style={{
                        marginTop: 6,
                        width: "100%",
                        background: "#0B1220",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 10px",
                      }}
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <option key={`end-${hour}`} value={hour}>
                          {formatHour(hour)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div style={{ marginTop: 8, color: "#67E8F9", fontSize: 12 }}>
                  Example business-hours protection: {formatHour(9)} - {formatHour(17)}
                </div>
              </div>

              <div style={{ ...cardStyle, background: "#13213A", margin: 0 }}>
                <h3 style={{ margin: "0 0 12px", color: "#E2E8F0", fontSize: 16 }}>
                  Notification Settings
                </h3>
                <div style={{ display: "grid", gap: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#CBD5E1" }}>
                    <input
                      type="checkbox"
                      checked={emailAlertsEnabled}
                      onChange={(event) => setEmailAlertsEnabled(event.target.checked)}
                    />
                    Email alerts on spike detected
                  </label>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, color: "#CBD5E1" }}>
                    <input
                      type="checkbox"
                      checked={slackNotificationsEnabled}
                      onChange={(event) => setSlackNotificationsEnabled(event.target.checked)}
                    />
                    Slack notifications
                  </label>
                  <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                    Slack webhook URL
                  </label>
                  <input
                    type="text"
                    value={notificationTarget}
                    onChange={(event) => setNotificationTarget(event.target.value)}
                    placeholder="https://hooks.slack.com/services/..."
                    style={{
                      background: "#0B1220",
                      color: "#E2E8F0",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  />
                  <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                    Email address for alerts
                  </label>
                  <input
                    type="email"
                    value={notificationEmail}
                    onChange={(event) => setNotificationEmail(event.target.value)}
                    placeholder="you@company.com"
                    style={{
                      background: "#0B1220",
                      color: "#E2E8F0",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  />
                  <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                    Billing contact email
                  </label>
                  <input
                    type="email"
                    value={billingContactEmail}
                    onChange={(event) => setBillingContactEmail(event.target.value)}
                    placeholder="billing@company.com"
                    style={{
                      background: "#0B1220",
                      color: "#E2E8F0",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "10px 12px",
                    }}
                  />
                  <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                    Billing fee percentage ({feePercentage.toFixed(1)}%)
                  </label>
                  <input
                    type="range"
                    min={15}
                    max={20}
                    step={0.5}
                    value={feePercentage}
                    onChange={(event) => setFeePercentage(Number(event.target.value))}
                  />
                  <label style={{ color: "#CBD5E1", fontSize: 12, fontWeight: 700 }}>
                    Carbon credit market price (${carbonCreditPrice.toFixed(0)}/credit)
                  </label>
                  <input
                    type="range"
                    min={15}
                    max={50}
                    step={1}
                    value={carbonCreditPrice}
                    onChange={(event) => setCarbonCreditPrice(Number(event.target.value))}
                    style={{ width: "100%", marginTop: 8, accentColor: "#34D399" }}
                  />
                  <div style={{ color: "#94A3B8", fontSize: 11 }}>
                    Used to estimate market value of credits (default $20; typical range about $15–25).
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                    <button
                      type="button"
                      onClick={() => void sendSettingsTestNotification()}
                      style={{
                        background: "#1D4ED8",
                        color: "#EFF6FF",
                        border: "1px solid #3B82F6",
                        borderRadius: 10,
                        padding: "10px 14px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Send test alert
                    </button>
                    <button
                      type="button"
                      onClick={() => void sendSettingsTestEmail()}
                      style={{
                        background: "#134E4A",
                        color: "#CCFBF1",
                        border: "1px solid #0EA5A0",
                        borderRadius: 10,
                        padding: "10px 14px",
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Send test email
                    </button>
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ color: "#22C55E", fontWeight: 700 }}>{saveConfirmation}</div>
                <button
                  onClick={saveSettings}
                  style={{
                    background: "#0EA5A0",
                    color: "#042F2E",
                    border: "none",
                    borderRadius: 10,
                    padding: "10px 16px",
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  Save Settings
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="stats-grid" style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
          <div style={cardStyle}>
            <div style={{ color: "#94A3B8", fontSize: 13 }}>Current Price</div>
            <div style={{ ...statValueStyle, color: priceColor }}>
              ${currentPrice?.price_per_mwh.toFixed(2) ?? "--"}
            </div>
            <div style={{ color: "#64748B", fontSize: 12 }}>/MWh</div>
          </div>
          <div style={cardStyle}>
            <div style={{ color: "#94A3B8", fontSize: 13 }}>Total Savings</div>
            <button
              type="button"
              onClick={() => setShowExactSavingsHeadline((prev) => !prev)}
              style={{
                ...statValueStyle,
                color: "#22C55E",
                background: "transparent",
                border: "none",
                padding: 0,
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "clip",
                minHeight: "1.25em",
                fontVariantNumeric: "tabular-nums",
                transition: "all 180ms ease",
              }}
              title="Toggle exact value"
            >
              <span style={{ transition: "opacity 180ms ease" }}>
                {showExactSavingsHeadline
                  ? `$${(savings?.total_cumulative_savings_usd ?? 0).toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : formatSavingsHeadline(savings?.total_cumulative_savings_usd)}
              </span>
            </button>
            <div className="mobile-savings-hint">tap for exact</div>
            <div style={{ color: "#64748B", fontSize: 12 }}>USD</div>
          </div>
          <div style={cardStyle}>
            <div style={{ color: "#94A3B8", fontSize: 13 }}>CO₂ avoided (today)</div>
            <div style={{ ...statValueStyle, color: "#14B8A6" }}>
              {(savings?.co2_epa_spike_kg_today ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 1,
              })}
            </div>
            <div style={{ color: "#64748B", fontSize: 12 }}>
              kg (EPA spike savings) ·{" "}
              {(savings?.carbon_credits_earned_today ?? 0).toLocaleString(undefined, {
                maximumFractionDigits: 4,
              })}{" "}
              credits
            </div>
          </div>
          <div style={cardStyle}>
            <div style={{ color: "#94A3B8", fontSize: 13 }}>Jobs Protected</div>
            <div style={{ ...statValueStyle, color: "#F87171" }}>
              {criticalCount}
            </div>
            <div style={{ color: "#64748B", fontSize: 12 }}>critical workloads</div>
          </div>
        </div>


        <div style={{ ...cardStyle, display: "grid", gap: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
              {agentDetail?.token_exists && agentDetail.is_online ? (
                <span
                  style={{
                    background: "#064E3B",
                    color: "#6EE7B7",
                    border: "1px solid #059669",
                    borderRadius: 999,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  ● Agent Connected
                </span>
              ) : agentDetail?.token_exists ? (
                <span
                  style={{
                    background: "#422006",
                    color: "#FDE68A",
                    border: "1px solid #D97706",
                    borderRadius: 999,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  ◐ Agent Offline
                </span>
              ) : (
                <span
                  style={{
                    background: "#1E293B",
                    color: "#94A3B8",
                    border: "1px solid #475569",
                    borderRadius: 999,
                    padding: "6px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                  }}
                >
                  ○ No Agent
                </span>
              )}
              {agentDetailLoading && (
                <span style={{ fontSize: 12, color: "#64748B" }}>Refreshing…</span>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {!agentDetail?.token_exists && (
                <a
                  href="https://github.com/eloplus/eloplus-agent#readme"
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: 13, color: "#67E8F9", alignSelf: "center" }}
                >
                  Setup docs
                </a>
              )}
              <button
                type="button"
                onClick={() => {
                  setAgentSetupOpen(true);
                  setAgentVerifyMessage(null);
                }}
                style={{
                  background: "#0EA5A0",
                  color: "#042F2E",
                  border: "none",
                  borderRadius: 10,
                  padding: "8px 14px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Connect Agent
              </button>
            </div>
          </div>

          {agentDetail?.token_exists && (
            <div
              style={{
                border: "1px solid #1F355B",
                borderRadius: 12,
                padding: 16,
                display: "grid",
                gap: 14,
                background: "#0F1E35",
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#F8FAFC" }}>
                    {agentDetail.token_name ?? "Agent"}
                  </div>
                  <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>
                    Scheduler: {agentDetail.scheduler ?? "—"}
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                  {(() => {
                    const badge = agentModeBadge(agentDetail.mode);
                    return (
                      <span
                        style={{
                          background: badge.bg,
                          color: badge.color,
                          border: `1px solid ${badge.border}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          fontSize: 12,
                          fontWeight: 700,
                        }}
                      >
                        {badge.label}
                      </span>
                    );
                  })()}
                  <span style={{ fontSize: 13, color: "#CBD5E1" }}>
                    {formatAgentResponseLabel(agentDetail.response_time_avg_ms)}
                  </span>
                </div>
              </div>

              {(agentDetail.response_time_recent_ms?.length ?? 0) > 0 && (
                <svg width="120" height="28" viewBox="0 0 120 28" aria-hidden>
                  {agentDetail.response_time_recent_ms.map((v, i, arr) => {
                    const max = Math.max(...arr, 1);
                    const x = (i / Math.max(arr.length - 1, 1)) * 110 + 5;
                    const y = 26 - (v / max) * 22;
                    const xPrev =
                      i > 0
                        ? ((i - 1) / Math.max(arr.length - 1, 1)) * 110 + 5
                        : x;
                    const yPrev =
                      i > 0 ? 26 - (arr[i - 1] / max) * 22 : y;
                    return (
                      <line
                        key={`rt-${i}`}
                        x1={xPrev}
                        y1={yPrev}
                        x2={x}
                        y2={y}
                        stroke="#14B8A6"
                        strokeWidth="2"
                      />
                    );
                  })}
                </svg>
              )}

              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, fontSize: 13 }}>
                <span style={{ color: agentDetail.webhook_registered ? "#6EE7B7" : "#94A3B8" }}>
                  {agentDetail.webhook_registered ? "⚡ Webhook Active" : "↻ Polling Only"}
                </span>
                <span style={{ color: "#94A3B8" }}>
                  Uptime {agentDetail.uptime_pct.toFixed(1)}% · {agentDetail.total_pauses} pauses /{" "}
                  {agentDetail.total_resumes} resumes
                </span>
              </div>

              {(agentDetail.jobs_currently_paused?.length ?? 0) > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <span style={{ fontSize: 12, color: "#94A3B8", alignSelf: "center" }}>Paused jobs:</span>
                  {agentDetail.jobs_currently_paused.map((jobId) => (
                    <span
                      key={jobId}
                      style={{
                        background: "#422006",
                        color: "#FDE68A",
                        border: "1px solid #D97706",
                        borderRadius: 8,
                        padding: "4px 10px",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      {jobId}
                    </span>
                  ))}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setShowAgentActivity((v) => !v);
                  setAgentActivityPage(1);
                }}
                style={{
                  justifySelf: "start",
                  background: "transparent",
                  color: "#67E8F9",
                  border: "1px solid #0EA5A0",
                  borderRadius: 10,
                  padding: "8px 14px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                {showAgentActivity ? "Hide Activity" : "View Activity"}
              </button>

              {showAgentActivity && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ color: "#94A3B8", textAlign: "left" }}>
                        <th style={{ padding: "8px 10px" }}>Time</th>
                        <th style={{ padding: "8px 10px" }}>Event</th>
                        <th style={{ padding: "8px 10px" }}>Jobs Affected</th>
                        <th style={{ padding: "8px 10px" }}>Price at Event</th>
                        <th style={{ padding: "8px 10px" }}>Est. Savings</th>
                        <th style={{ padding: "8px 10px" }}>Response Time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedAgentActivity.length === 0 ? (
                        <tr>
                          <td colSpan={6} style={{ padding: 12, color: "#64748B" }}>
                            No pause/resume events yet.
                          </td>
                        </tr>
                      ) : (
                        pagedAgentActivity.map((ev) => {
                          const p = ev.payload || {};
                          const jobs = Array.isArray(p.jobs_affected)
                            ? (p.jobs_affected as string[]).join(", ")
                            : "—";
                          const price =
                            typeof p.price === "number"
                              ? `$${p.price.toFixed(2)}/MWh`
                              : "—";
                          const savings =
                            typeof p.savings_estimate === "number"
                              ? `$${p.savings_estimate.toFixed(2)}`
                              : "—";
                          const rt =
                            typeof p.response_time_ms === "number"
                              ? `${Math.round(p.response_time_ms)} ms`
                              : "—";
                          return (
                            <tr key={`${ev.timestamp}-${ev.event_type}`} style={agentEventRowStyle(ev.event_type)}>
                              <td style={{ padding: "8px 10px", color: "#E2E8F0" }}>
                                {new Date(ev.timestamp).toLocaleString()}
                              </td>
                              <td style={{ padding: "8px 10px" }}>{formatAgentEventLabel(ev.event_type)}</td>
                              <td style={{ padding: "8px 10px", color: "#CBD5E1" }}>{jobs || "—"}</td>
                              <td style={{ padding: "8px 10px" }}>{price}</td>
                              <td style={{ padding: "8px 10px", color: "#22C55E" }}>{savings}</td>
                              <td style={{ padding: "8px 10px" }}>{rt}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  {agentActivityEvents.length > 10 && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 10,
                      }}
                    >
                      <button
                        type="button"
                        disabled={agentActivityPage <= 1}
                        onClick={() => setAgentActivityPage((p) => Math.max(1, p - 1))}
                        style={{
                          background: "#1E293B",
                          color: "#E2E8F0",
                          border: "1px solid #334155",
                          borderRadius: 8,
                          padding: "6px 12px",
                          cursor: agentActivityPage <= 1 ? "not-allowed" : "pointer",
                        }}
                      >
                        Previous
                      </button>
                      <span style={{ fontSize: 12, color: "#94A3B8" }}>
                        Page {agentActivityPage} of {agentActivityTotalPages}
                      </span>
                      <button
                        type="button"
                        disabled={agentActivityPage >= agentActivityTotalPages}
                        onClick={() =>
                          setAgentActivityPage((p) => Math.min(agentActivityTotalPages, p + 1))
                        }
                        style={{
                          background: "#1E293B",
                          color: "#E2E8F0",
                          border: "1px solid #334155",
                          borderRadius: 8,
                          padding: "6px 12px",
                          cursor:
                            agentActivityPage >= agentActivityTotalPages
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {agentSetupOpen && (
          <div
            role="dialog"
            aria-modal
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(2, 6, 23, 0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
              padding: 16,
            }}
            onClick={() => setAgentSetupOpen(false)}
          >
            <div
              style={{
                ...cardStyle,
                maxWidth: 560,
                width: "100%",
                display: "grid",
                gap: 14,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Connect EloPlus Agent</h2>
              <p style={{ margin: 0, fontSize: 13, color: "#94A3B8" }}>
                Install the on-prem agent to pause flexible workloads when grid prices spike.
              </p>

              <div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 6 }}>Step 1 — Install</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <code
                    style={{
                      flex: 1,
                      background: "#0A1628",
                      padding: 10,
                      borderRadius: 8,
                      fontSize: 12,
                      color: "#E2E8F0",
                    }}
                  >
                    pip install eloplus-agent
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyToClipboard("pip install eloplus-agent")}
                    style={{
                      background: "#1E293B",
                      color: "#E2E8F0",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 6 }}>
                  Step 2 — Token {!agentSetupToken && "(generate below)"}
                </div>
                {!agentSetupToken ? (
                  <button
                    type="button"
                    disabled={agentSetupBusy}
                    onClick={() => void generateAgentToken()}
                    style={{
                      background: "#0EA5A0",
                      color: "#042F2E",
                      border: "none",
                      borderRadius: 10,
                      padding: "10px 14px",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    {agentSetupBusy ? "Generating…" : "Generate agent token"}
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <code
                      style={{
                        flex: 1,
                        background: "#0A1628",
                        padding: 10,
                        borderRadius: 8,
                        fontSize: 12,
                        color: "#E2E8F0",
                        wordBreak: "break-all",
                      }}
                    >
                      eloplus init --token {agentSetupToken}
                    </code>
                    <button
                      type="button"
                      onClick={() =>
                        void copyToClipboard(`eloplus init --token ${agentSetupToken}`)
                      }
                      style={{
                        background: "#1E293B",
                        color: "#E2E8F0",
                        border: "1px solid #334155",
                        borderRadius: 8,
                        padding: "8px 12px",
                        cursor: "pointer",
                      }}
                    >
                      Copy
                    </button>
                  </div>
                )}
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 6 }}>Step 3 — Start</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <code
                    style={{
                      flex: 1,
                      background: "#0A1628",
                      padding: 10,
                      borderRadius: 8,
                      fontSize: 12,
                      color: "#E2E8F0",
                    }}
                  >
                    eloplus start
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyToClipboard("eloplus start")}
                    style={{
                      background: "#1E293B",
                      color: "#E2E8F0",
                      border: "1px solid #334155",
                      borderRadius: 8,
                      padding: "8px 12px",
                      cursor: "pointer",
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 6 }}>Step 4 — Verify</div>
                <button
                  type="button"
                  disabled={agentVerifying || !agentSetupToken}
                  onClick={startAgentVerify}
                  style={{
                    background: agentDetail?.is_online ? "#064E3B" : "#1E293B",
                    color: agentDetail?.is_online ? "#6EE7B7" : "#E2E8F0",
                    border: `1px solid ${agentDetail?.is_online ? "#059669" : "#334155"}`,
                    borderRadius: 10,
                    padding: "10px 14px",
                    fontWeight: 700,
                    cursor: agentVerifying || !agentSetupToken ? "not-allowed" : "pointer",
                  }}
                >
                  {agentVerifying ? "Verifying…" : "Verify connection"}
                </button>
                {agentVerifyMessage && (
                  <div
                    style={{
                      marginTop: 8,
                      fontSize: 13,
                      color: agentDetail?.is_online ? "#6EE7B7" : "#94A3B8",
                    }}
                  >
                    {agentVerifyMessage}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setAgentSetupOpen(false)}
                style={{
                  justifySelf: "end",
                  background: "transparent",
                  color: "#94A3B8",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}

        {backtestModalOpen && (
          <div
            role="dialog"
            aria-modal
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(2, 6, 23, 0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
              padding: 16,
            }}
            onClick={() => !backtestRunning && setBacktestModalOpen(false)}
          >
            <div
              style={{
                ...cardStyle,
                maxWidth: 480,
                width: "100%",
                display: "grid",
                gap: 14,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Run Backtest</h2>
              <p style={{ margin: 0, fontSize: 13, color: "#94A3B8" }}>
                Simulate historical grid prices against a pause threshold to generate a concrete
                savings figure.
              </p>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Region</span>
                <select
                  value={backtestRegion}
                  onChange={(e) => onBacktestRegionChange(e.target.value as BacktestRegion)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                >
                  <option value="ERCOT">ERCOT</option>
                  <option value="PJM">PJM</option>
                  <option value="CAISO">CAISO</option>
                </select>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Cluster size (MW)</span>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={backtestClusterMw}
                  onChange={(e) => setBacktestClusterMw(Number(e.target.value))}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Spike threshold ($/MWh)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={backtestThreshold}
                  onChange={(e) => setBacktestThreshold(Number(e.target.value))}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Months of history</span>
                <input
                  type="number"
                  min={1}
                  max={12}
                  step={1}
                  value={backtestMonths}
                  onChange={(e) =>
                    setBacktestMonths(Math.max(1, Math.min(12, Number(e.target.value))))
                  }
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
              </label>

              {backtestError && (
                <div style={{ color: "#FCA5A5", fontSize: 13 }}>{backtestError}</div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setBacktestModalOpen(false)}
                  disabled={backtestRunning}
                  style={{
                    background: "transparent",
                    color: "#94A3B8",
                    border: "none",
                    cursor: backtestRunning ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void runBacktest()}
                  disabled={backtestRunning}
                  style={{
                    background: "#7C3AED",
                    color: "#F5F3FF",
                    border: "1px solid #A78BFA",
                    borderRadius: 10,
                    padding: "10px 18px",
                    fontWeight: 800,
                    cursor: backtestRunning ? "wait" : "pointer",
                    opacity: backtestRunning ? 0.75 : 1,
                  }}
                >
                  {backtestRunning ? "Running…" : "Run Analysis"}
                </button>
              </div>
            </div>
          </div>
        )}

        {backtestResult && (
          <BacktestChart result={backtestResult} defaultCompanyName={user?.company_name} />
        )}

        {dryRunModalOpen && (
          <div
            role="dialog"
            aria-modal
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(2, 6, 23, 0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 50,
              padding: 16,
            }}
            onClick={() => !dryRunRunning && setDryRunModalOpen(false)}
          >
            <div
              style={{
                ...cardStyle,
                maxWidth: 480,
                width: "100%",
                display: "grid",
                gap: 14,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Run Dry Run</h2>
              <p style={{ margin: 0, fontSize: 13, color: "#94A3B8" }}>
                See what EloPlus would have done last month — zero risk. This is a read-only
                simulation: no pause command is ever issued and your cluster is never contacted.
              </p>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Month to simulate</span>
                <input
                  type="month"
                  value={dryRunMonth}
                  onChange={(e) => setDryRunMonth(e.target.value)}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Cluster size (MW)</span>
                <input
                  type="number"
                  min={0.1}
                  step={0.1}
                  value={dryRunClusterMw}
                  onChange={(e) => setDryRunClusterMw(Number(e.target.value))}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
                <span style={{ fontSize: 11, color: "#64748B" }}>
                  Used only if you have no jobs registered yet — otherwise your real registered
                  FLEXIBLE jobs are simulated (CRITICAL jobs are never touched).
                </span>
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ fontSize: 12, color: "#94A3B8" }}>Spike threshold ($/MWh)</span>
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={dryRunThreshold}
                  onChange={(e) => setDryRunThreshold(Number(e.target.value))}
                  style={{
                    padding: "10px 12px",
                    borderRadius: 8,
                    border: "1px solid #334155",
                    background: "#0B1220",
                    color: "#F8FAFC",
                  }}
                />
              </label>

              {dryRunError && <div style={{ color: "#FCA5A5", fontSize: 13 }}>{dryRunError}</div>}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setDryRunModalOpen(false)}
                  disabled={dryRunRunning}
                  style={{
                    background: "transparent",
                    color: "#94A3B8",
                    border: "none",
                    cursor: dryRunRunning ? "not-allowed" : "pointer",
                    fontWeight: 600,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void runDryRun()}
                  disabled={dryRunRunning}
                  style={{
                    background: "#0369A1",
                    color: "#F0F9FF",
                    border: "1px solid #38BDF8",
                    borderRadius: 10,
                    padding: "10px 18px",
                    fontWeight: 800,
                    cursor: dryRunRunning ? "wait" : "pointer",
                    opacity: dryRunRunning ? 0.75 : 1,
                  }}
                >
                  {dryRunRunning ? "Simulating…" : "Run Analysis"}
                </button>
              </div>
            </div>
          </div>
        )}

        {dryRunResult && (
          <DryRunPanel result={dryRunResult} defaultCompanyName={user?.company_name} />
        )}

        <div className="price-chart-card" style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, color: "#F8FAFC" }}>Price Trend (24h)</h2>
          <div className="chart-wrap" style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#243B63" strokeDasharray="4 4" />
                <XAxis dataKey="label" stroke="#94A3B8" minTickGap={22} />
                <YAxis stroke="#94A3B8" />
                <Tooltip
                  contentStyle={{
                    background: "#0F1E35",
                    border: "1px solid #1F355B",
                    borderRadius: 10,
                    color: "#E2E8F0",
                  }}
                  formatter={(
                    value: number | string | ReadonlyArray<number | string> | undefined
                  ) => {
                    const numericValue =
                      typeof value === "number"
                        ? value
                        : typeof value === "string"
                        ? Number(value)
                        : Array.isArray(value)
                        ? Number(value[0])
                        : 0;
                    return [`$${numericValue.toFixed(2)} /MWh`, "Price"];
                  }}
                />
                <ReferenceLine
                  y={savedPriceThreshold}
                  stroke="#EF4444"
                  strokeDasharray="6 4"
                  label={{
                    value: `Optimization Threshold $${savedPriceThreshold}`,
                    fill: "#FCA5A5",
                    position: "insideTopRight",
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="price_per_mwh"
                  stroke="#0EA5A0"
                  strokeWidth={3}
                  dot={(props) => {
                    const payload = props.payload as PriceSignal;
                    return (
                      <circle
                        cx={props.cx}
                        cy={props.cy}
                        r={payload.is_spike ? 4 : 2.5}
                        fill={payload.is_spike ? "#EF4444" : "#2DD4BF"}
                        stroke="none"
                      />
                    );
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, color: "#F8FAFC" }}>Forecast</h2>
          <div style={{ display: "grid", gap: 14 }}>
            <div
              style={{
                display: "inline-block",
                width: "fit-content",
                padding: "8px 12px",
                borderRadius: 999,
                fontWeight: 800,
                ...FORECAST_BAND_BADGE[forecast?.current_band ?? "low"],
                border: "1px solid #334155",
              }}
            >
              Spike probability: {forecast ? `${forecast.current_score.toFixed(0)}%` : "—"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, fontSize: 11, color: "#94A3B8" }}>
              {(["low", "elevated", "high", "imminent"] as const).map((band) => (
                <span key={band} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 2,
                      background: FORECAST_BAND_BAR_FILL[band],
                    }}
                  />
                  {band}
                </span>
              ))}
            </div>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <BarChart data={forecast?.curve_24h ?? []}>
                  <CartesianGrid stroke="#243B63" strokeDasharray="4 4" />
                  <XAxis dataKey="hour" tickFormatter={(v) => new Date(String(v)).getHours().toString()} />
                  <YAxis domain={[0, 100]} />
                  <Tooltip
                    formatter={(
                      v: number | string | ReadonlyArray<number | string> | undefined
                    ) => [`${Number(Array.isArray(v) ? v[0] : v ?? 0).toFixed(0)}%`, "Risk"]}
                  />
                  <Bar dataKey="score" radius={[4, 4, 0, 0]}>
                    {(forecast?.curve_24h ?? []).map((entry, i) => (
                      <Cell key={`fc-${entry.hour}-${i}`} fill={FORECAST_BAND_BAR_FILL[entry.band]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div style={{ color: "#94A3B8", fontSize: 12, marginBottom: 10 }}>Factors driving this forecast</div>
              <div style={{ display: "grid", gap: 12 }}>
                {buildForecastFactorRows(forecast?.factors).map((row) => (
                  <div
                    key={row.label}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "minmax(132px, 30%) 1fr",
                      gap: 14,
                      alignItems: "start",
                      fontSize: 13,
                    }}
                  >
                    <span style={{ color: "#94A3B8", fontWeight: 600 }}>{row.label}</span>
                    <span style={{ color: "#E2E8F0", lineHeight: 1.45 }}>{row.description}</span>
                  </div>
                ))}
                {!forecast && (
                  <div style={{ color: "#64748B", fontSize: 13 }}>Loading forecast…</div>
                )}
              </div>
            </div>
            <div>
              <div style={{ color: "#94A3B8", fontSize: 12, marginBottom: 6 }}>Last 5 predictions</div>
              <div style={{ display: "grid", gap: 4, color: "#CBD5E1", fontSize: 12 }}>
                {forecastHistory.slice(0, 5).map((h) => (
                  <div key={`${h.timestamp}-${h.predicted_probability}`}>
                    {new Date(h.timestamp).toLocaleString()} — {h.predicted_probability.toFixed(0)}% —{" "}
                    {h.actual_spike === null ? "pending" : h.actual_spike ? "hit" : "miss"}
                  </div>
                ))}
                {forecastHistory.length === 0 && <div>No predictions yet.</div>}
              </div>
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, color: "#F8FAFC" }}>Carbon impact</h2>
          <div style={{ color: "#94A3B8", fontSize: 12, marginBottom: 14, lineHeight: 1.45 }}>
            Spike-response load avoided × EPA grid factor (0.386 kg CO₂/kWh). One carbon credit = one metric ton CO₂
            avoided. Values use your settings price (${carbonSummary?.credit_price_usd?.toFixed(0) ?? "—"}/credit).
          </div>
          <div
            style={{
              display: "grid",
              gap: 14,
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              alignItems: "start",
            }}
          >
            <div>
              <div style={{ color: "#94A3B8", fontSize: 12 }}>CO₂ avoided this month</div>
              <div style={{ ...statValueStyle, color: "#5EEAD4", fontSize: "clamp(1rem, 4vw, 22px)" }}>
                {carbonSummary ? `${carbonSummary.month_co2_kg.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg` : "—"}
              </div>
              <div style={{ color: "#64748B", fontSize: 12 }}>
                {carbonSummary ? `${carbonSummary.month_co2_metric_tons.toFixed(3)} metric tons` : ""}
              </div>
            </div>
            <div>
              <div style={{ color: "#94A3B8", fontSize: 12 }}>Credits earned this month</div>
              <div style={{ ...statValueStyle, color: "#34D399", fontSize: "clamp(1rem, 4vw, 22px)" }}>
                {carbonSummary ? carbonSummary.month_carbon_credits.toFixed(3) : "—"}
              </div>
              <div style={{ color: "#64748B", fontSize: 12 }}>estimated credit value</div>
              <div style={{ color: "#A7F3D0", fontSize: 14, fontWeight: 700 }}>
                {carbonSummary ? `$${carbonSummary.month_carbon_value_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
              </div>
            </div>
            <div>
              <div style={{ color: "#94A3B8", fontSize: 12 }}>Cumulative credits</div>
              <div style={{ ...statValueStyle, color: "#6EE7B7", fontSize: "clamp(1rem, 4vw, 22px)" }}>
                {carbonSummary ? carbonSummary.cumulative_carbon_credits.toFixed(3) : "—"}
              </div>
              <div style={{ color: "#64748B", fontSize: 12 }}>lifetime est. value</div>
              <div style={{ color: "#A7F3D0", fontSize: 14, fontWeight: 700 }}>
                {carbonSummary ? `$${carbonSummary.cumulative_carbon_value_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", color: "#94A3B8", fontSize: 12, marginBottom: 6 }}>
              <span>Progress toward next full credit (1 metric ton)</span>
              <span>{carbonSummary ? `${carbonSummary.progress_toward_next_credit_pct.toFixed(0)}%` : "—"}</span>
            </div>
            <div
              style={{
                height: 10,
                borderRadius: 999,
                background: "#0B1220",
                border: "1px solid #1F355B",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.min(100, carbonSummary?.progress_toward_next_credit_pct ?? 0)}%`,
                  background: "linear-gradient(90deg, #0EA5A0, #34D399)",
                  transition: "width 0.4s ease",
                }}
              />
            </div>
          </div>
        </div>

          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Billing</h2>
              <button
                type="button"
                onClick={() => void fetchBillingInvoices()}
                style={{
                  border: "1px solid #334155",
                  background: "#0B1220",
                  color: "#E2E8F0",
                  borderRadius: 8,
                  padding: "8px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Refresh invoices
              </button>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#94A3B8", borderBottom: "1px solid #1F355B" }}>
                    <th style={{ padding: "10px 8px" }}>Invoice</th>
                    <th style={{ padding: "10px 8px" }}>Period</th>
                    <th style={{ padding: "10px 8px" }}>Savings</th>
                    <th style={{ padding: "10px 8px" }}>Fee</th>
                    <th style={{ padding: "10px 8px" }}>Status</th>
                    <th style={{ padding: "10px 8px" }}>PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {billingInvoices.map((inv) => {
                    const statusColor =
                      inv.status === "paid" ? "#22C55E" : inv.status === "overdue" ? "#EF4444" : "#FACC15";
                    return (
                      <tr key={inv.invoice_id} style={{ borderBottom: "1px solid #1F355B" }}>
                        <td style={{ padding: "10px 8px", color: "#E2E8F0", fontWeight: 700 }}>{inv.invoice_id}</td>
                        <td style={{ padding: "10px 8px", color: "#CBD5E1" }}>{inv.billing_period}</td>
                        <td style={{ padding: "10px 8px", color: "#22C55E" }}>
                          ${inv.total_savings.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: "10px 8px", color: "#67E8F9" }}>
                          ${inv.fee_amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: "10px 8px", color: statusColor, fontWeight: 700 }}>
                          {inv.status.toUpperCase()}
                        </td>
                        <td style={{ padding: "10px 8px" }}>
                          <button
                            type="button"
                            onClick={() => void downloadInvoicePdf(inv.invoice_id)}
                            style={{
                              border: "1px solid #0EA5A0",
                              background: "#0F172A",
                              color: "#99F6E4",
                              borderRadius: 8,
                              padding: "6px 10px",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Download
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {billingInvoices.length === 0 && (
                <div style={{ color: "#94A3B8", fontSize: 13, padding: "8px 4px" }}>
                  No invoices yet. Monthly automation sends invoices on the 1st.
                </div>
              )}
            </div>
          </div>

          <div ref={auditSectionRef} style={cardStyle}>
          <h2 style={{ margin: "0 0 14px", fontSize: 20, color: "#F8FAFC" }}>Compute Jobs</h2>
          <div className="jobs-table-desktop" style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ color: "#94A3B8", textAlign: "left" }}>
                  <th style={{ padding: "10px 8px" }}>Job Name</th>
                  <th style={{ padding: "10px 8px" }}>Priority</th>
                  <th style={{ padding: "10px 8px" }}>Status</th>
                  <th style={{ padding: "10px 8px" }}>Power (kW)</th>
                  <th style={{ padding: "10px 8px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <tr key={job.id} style={{ borderTop: "1px solid #1F355B" }}>
                    <td style={{ padding: "10px 8px", color: "#E2E8F0" }}>{job.name}</td>
                    <td style={{ padding: "10px 8px" }}>
                      <span
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#fff",
                          background: job.priority === "CRITICAL" ? "#DC2626" : "#2563EB",
                        }}
                      >
                        {job.priority}
                      </span>
                    </td>
                    <td style={{ padding: "10px 8px" }}>
                      <span
                        style={{
                          padding: "5px 9px",
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#fff",
                          background: job.status === "RUNNING" ? "#16A34A" : "#EAB308",
                        }}
                      >
                        {job.status}
                      </span>
                    </td>
                    <td style={{ padding: "10px 8px", color: "#CBD5E1" }}>{job.power_kw.toFixed(2)}</td>
                    <td style={{ padding: "10px 8px" }}>
                      {jobActionSuccess[job.id] && !jobActionPending[job.id] ? (
                        <span
                          style={{
                            display: "inline-block",
                            padding: "8px 12px",
                            borderRadius: 8,
                            fontWeight: 700,
                            fontSize: 13,
                            color: "#BBF7D0",
                            background: "rgba(22, 163, 74, 0.18)",
                            border: "1px solid rgba(34, 197, 94, 0.5)",
                          }}
                        >
                          {jobActionSuccess[job.id]}
                        </span>
                      ) : job.status === "RUNNING" ? (
                        job.priority === "CRITICAL" ? null : (
                        <button
                          disabled={jobActionPending[job.id] === true}
                          onClick={() => handleJobAction(job.id, "pause")}
                          style={{
                            background: "#EAB308",
                            color: "#111827",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 12px",
                            cursor: jobActionPending[job.id] ? "wait" : "pointer",
                            fontWeight: 700,
                            opacity: jobActionPending[job.id] ? 0.7 : 1,
                          }}
                        >
                          Pause
                        </button>
                        )
                      ) : (
                        <button
                          disabled={jobActionPending[job.id] === true}
                          onClick={() => handleJobAction(job.id, "resume")}
                          style={{
                            background: "#22C55E",
                            color: "#052E16",
                            border: "none",
                            borderRadius: 8,
                            padding: "8px 12px",
                            cursor: jobActionPending[job.id] ? "wait" : "pointer",
                            fontWeight: 700,
                            opacity: jobActionPending[job.id] ? 0.7 : 1,
                          }}
                        >
                          Resume
                        </button>
                      )}
                      {jobActionErrors[job.id] ? (
                        <div style={{ marginTop: 6, color: "#FCA5A5", fontSize: 12 }}>
                          {jobActionErrors[job.id]}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="jobs-mobile-cards">
            {jobs.map((job) => (
              <div key={`mobile-${job.id}`} className="job-card-mobile">
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F8FAFC" }}>{job.name}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <span
                    style={{
                      padding: "5px 9px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#fff",
                      background: job.priority === "CRITICAL" ? "#DC2626" : "#2563EB",
                    }}
                  >
                    {job.priority}
                  </span>
                  <span
                    style={{
                      padding: "5px 9px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#fff",
                      background: job.status === "RUNNING" ? "#16A34A" : "#EAB308",
                    }}
                  >
                    {job.status}
                  </span>
                </div>
                <div style={{ color: "#94A3B8", fontSize: 12 }}>Power: {job.power_kw.toFixed(2)} kW</div>
                <div style={{ display: "grid", gap: 8 }}>
                  {jobActionSuccess[job.id] && !jobActionPending[job.id] ? (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "8px 12px",
                        borderRadius: 8,
                        fontWeight: 700,
                        fontSize: 13,
                        color: "#BBF7D0",
                        background: "rgba(22, 163, 74, 0.18)",
                        border: "1px solid rgba(34, 197, 94, 0.5)",
                      }}
                    >
                      {jobActionSuccess[job.id]}
                    </span>
                  ) : job.status === "RUNNING" ? (
                    job.priority === "CRITICAL" ? null : (
                    <button
                      disabled={jobActionPending[job.id] === true}
                      onClick={() => handleJobAction(job.id, "pause")}
                      style={{
                        background: "#EAB308",
                        color: "#111827",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 12px",
                        cursor: jobActionPending[job.id] ? "wait" : "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Pause
                    </button>
                    )
                  ) : (
                    <button
                      disabled={jobActionPending[job.id] === true}
                      onClick={() => handleJobAction(job.id, "resume")}
                      style={{
                        background: "#22C55E",
                        color: "#052E16",
                        border: "none",
                        borderRadius: 8,
                        padding: "10px 12px",
                        cursor: jobActionPending[job.id] ? "wait" : "pointer",
                        fontWeight: 700,
                      }}
                    >
                      Resume
                    </button>
                  )}
                  {jobActionErrors[job.id] ? (
                    <div style={{ color: "#FCA5A5", fontSize: 12 }}>
                      {jobActionErrors[job.id]}
                    </div>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={cardStyle}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: 12,
              alignItems: "center",
              marginBottom: 14,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Audit Log</h2>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
              {(["all", "system", "human", "settings"] as const).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setAuditFilter(key)}
                  style={{
                    padding: "6px 12px",
                    borderRadius: 8,
                    border: auditFilter === key ? "2px solid #0EA5A0" : "1px solid #334155",
                    background: auditFilter === key ? "rgba(14,165,160,0.15)" : "#0B1220",
                    color: "#E2E8F0",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                >
                  {key === "all"
                    ? "All"
                    : key === "system"
                      ? "System"
                      : key === "human"
                        ? "Manual"
                        : "Settings"}
                </button>
              ))}
              <input
                type="date"
                value={auditFromDate}
                onChange={(e) => setAuditFromDate(e.target.value)}
                title="From date (UTC range)"
                style={{
                  background: "#0B1220",
                  color: "#E2E8F0",
                  border: "1px solid #334155",
                  borderRadius: 8,
                  padding: 6,
                }}
              />
              <input
                type="date"
                value={auditToDate}
                onChange={(e) => setAuditToDate(e.target.value)}
                title="To date (UTC range)"
                style={{
                  background: "#0B1220",
                  color: "#E2E8F0",
                  border: "1px solid #334155",
                  borderRadius: 8,
                  padding: 6,
                }}
              />
              <button
                type="button"
                onClick={() => void fetchAuditLog()}
                style={{
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "1px solid #334155",
                  background: "#0B1220",
                  color: "#E2E8F0",
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                Refresh
              </button>
            </div>
          </div>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#94A3B8" }}>
            Tamper-resistant trail of sign-ins, settings changes, optimizations, watchdog runs, and report exports.
          </p>
          {auditLoading ? (
            <div style={{ color: "#94A3B8" }}>Loading audit trail…</div>
          ) : (
            <div className="audit-desktop-table" style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr
                    style={{
                      color: "#94A3B8",
                      textAlign: "left",
                      position: "sticky",
                      top: 0,
                      background: "#13213A",
                    }}
                  >
                    <th style={{ padding: "8px 8px" }}>Event</th>
                    <th style={{ padding: "8px 8px" }}>Actor</th>
                    <th style={{ padding: "8px 8px" }}>Time (UTC)</th>
                    <th style={{ padding: "8px 8px" }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedAuditEvents.map((ev) => {
                    const acc = auditAccent(ev);
                    return (
                      <tr key={ev.id} style={{ borderTop: "1px solid #1F355B", background: acc.rowBg }}>
                        <td style={{ padding: "10px 8px", verticalAlign: "top" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "4px 8px",
                              borderRadius: 6,
                              fontSize: 10,
                              fontWeight: 800,
                              background: acc.badgeBg,
                              color: "#fff",
                            }}
                          >
                            {ev.event_type}
                          </span>
                        </td>
                        <td style={{ padding: "10px 8px", color: "#E2E8F0", fontWeight: 600 }}>
                          {ev.actor === "system" ? "System" : "You"}
                        </td>
                        <td style={{ padding: "10px 8px", color: "#94A3B8", whiteSpace: "nowrap" }}>
                          {new Date(ev.timestamp).toISOString().replace("T", " ").slice(0, 19)}
                        </td>
                        <td style={{ padding: "10px 8px", color: "#CBD5E1", maxWidth: 480 }}>
                          {ev.description}
                          {ev.ip_address ? (
                            <span style={{ display: "block", fontSize: 11, color: "#64748B", marginTop: 4 }}>
                              IP {ev.ip_address}
                            </span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {pagedAuditEvents.length === 0 && !auditLoading && (
                <div style={{ color: "#64748B", padding: 16 }}>No audit events match the current filters.</div>
              )}
            </div>
          )}
          {!auditLoading && (
            <div className="audit-mobile-list">
              {pagedAuditEvents.map((ev) => {
                const acc = auditAccent(ev);
                return (
                  <div key={`mobile-audit-${ev.id}`} className="audit-item-mobile">
                    <span
                      style={{
                        display: "inline-block",
                        padding: "4px 8px",
                        borderRadius: 6,
                        fontSize: 10,
                        fontWeight: 800,
                        background: acc.badgeBg,
                        color: "#fff",
                        width: "fit-content",
                      }}
                    >
                      {ev.event_type}
                    </span>
                    <div style={{ color: "#CBD5E1", fontSize: 13 }}>{ev.description}</div>
                    <div style={{ color: "#94A3B8", fontSize: 12 }}>
                      {new Date(ev.timestamp).toLocaleString()}
                    </div>
                  </div>
                );
              })}
              {pagedAuditEvents.length === 0 && (
                <div style={{ color: "#64748B", padding: 12 }}>No audit events match the current filters.</div>
              )}
            </div>
          )}
          {!auditLoading && auditEvents.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
              <button
                type="button"
                onClick={() => setAuditPage((prev) => Math.max(1, prev - 1))}
                disabled={auditPage <= 1}
                style={{
                  border: "1px solid #334155",
                  background: auditPage <= 1 ? "#111827" : "#0B1220",
                  color: auditPage <= 1 ? "#6B7280" : "#E2E8F0",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: auditPage <= 1 ? "not-allowed" : "pointer",
                  fontWeight: 700,
                }}
              >
                ← Prev
              </button>
              <div style={{ color: "#94A3B8", fontSize: 13 }}>
                Page {auditPage} of {totalAuditPages}
              </div>
              <button
                type="button"
                onClick={() => setAuditPage((prev) => Math.min(totalAuditPages, prev + 1))}
                disabled={auditPage >= totalAuditPages}
                style={{
                  border: "1px solid #334155",
                  background: auditPage >= totalAuditPages ? "#111827" : "#0B1220",
                  color: auditPage >= totalAuditPages ? "#6B7280" : "#E2E8F0",
                  borderRadius: 8,
                  padding: "8px 12px",
                  cursor: auditPage >= totalAuditPages ? "not-allowed" : "pointer",
                  fontWeight: 700,
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>

        <div style={{ ...cardStyle, display: "grid", gap: 12 }}>
          <div className="optimization-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Optimization Control</h2>
            <div className="optimization-actions" style={{ display: "flex", gap: 10 }}>
              <button
                className="control-btn"
                onClick={() => setDryRunModalOpen(true)}
                title="See what EloPlus would have done last month — zero risk"
                style={{
                  background: "#0369A1",
                  color: "#F0F9FF",
                  border: "1px solid #38BDF8",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Run Dry Run
              </button>
              <button
                className="control-btn"
                onClick={() => setBacktestModalOpen(true)}
                style={{
                  background: "#7C3AED",
                  color: "#F5F3FF",
                  border: "1px solid #A78BFA",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Run Backtest
              </button>
              <button
                className="control-btn"
                onClick={downloadMonthlyReport}
                disabled={reportDownloading}
                style={{
                  background: "#1D4ED8",
                  color: "#EFF6FF",
                  border: "1px solid #3B82F6",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 800,
                  cursor: reportDownloading ? "wait" : "pointer",
                  opacity: reportDownloading ? 0.75 : 1,
                }}
              >
                {reportDownloading ? "Generating..." : "Download Report"}
              </button>
              <button
                className="control-btn"
                onClick={runOptimizationNow}
                style={{
                  background: "#0EA5A0",
                  color: "#042F2E",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Run Optimization Now
              </button>
              <button
                className="control-btn"
                onClick={simulateSpike}
                style={{
                  background: "linear-gradient(135deg, #F97316 0%, #DC2626 100%)",
                  color: "#FFF7ED",
                  border: "1px solid #FB923C",
                  borderRadius: 10,
                  padding: "10px 16px",
                  fontWeight: 800,
                  cursor: "pointer",
                  boxShadow: "0 8px 16px rgba(220, 38, 38, 0.35)",
                }}
              >
                ⚡ Simulate Spike
              </button>
            </div>
          </div>
          {lastOptimization ? (
            <div style={{ color: "#CBD5E1", fontSize: 14, lineHeight: 1.6 }}>
              <div>
                Last Run: {new Date(lastOptimization.timestamp).toLocaleString()} | Price: $
                {lastOptimization.price_per_mwh.toFixed(2)} /MWh | Saved: $
                {lastOptimization.estimated_savings_usd.toFixed(2)}
              </div>
              <div style={{ marginTop: 8, color: "#94A3B8" }}>
                Actions: {lastOptimization.actions_taken.join(" | ")}
              </div>
            </div>
          ) : (
            <div style={{ color: "#94A3B8" }}>No optimization run recorded yet.</div>
          )}
        </div>

        <div style={{ ...cardStyle, display: "grid", gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: "#F8FAFC" }}>Live Optimization Event Log</h2>
          <div style={{ display: "grid", gap: 8 }}>
            {eventLog.length === 0 ? (
              <div style={{ color: "#94A3B8" }}>No optimization events yet.</div>
            ) : (
              eventLog.map((event, index) => (
                <div
                  key={`${event.timestamp}-${index}`}
                  style={{
                    borderRadius: 10,
                    border: event.is_spike ? "1px solid #7F1D1D" : "1px solid #14532D",
                    background: event.is_spike ? "rgba(127, 29, 29, 0.35)" : "rgba(20, 83, 45, 0.28)",
                    padding: "10px 12px",
                    display: "grid",
                    gap: 4,
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <span style={{ color: "#E2E8F0", fontWeight: 700 }}>
                      {new Date(event.timestamp).toLocaleString()}
                    </span>
                    <span style={{ color: event.is_spike ? "#FCA5A5" : "#86EFAC", fontWeight: 700 }}>
                      ${event.price_per_mwh.toFixed(2)} /MWh
                    </span>
                    <span style={{ color: "#A7F3D0", fontWeight: 700 }}>
                      Saved ${event.estimated_savings_usd.toFixed(2)}
                    </span>
                  </div>
                  <div style={{ color: "#CBD5E1", fontSize: 13 }}>
                    Actions: {event.actions_taken.join(" | ")}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {(loading || error) && (
          <div style={{ color: error ? "#F87171" : "#67E8F9", fontSize: 14 }}>
            {loading ? "Loading live dashboard..." : error}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
